import * as React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import { DateRangePicker, DollarFormatter, Page, PercentageFormatter } from '@src/components';
import {
  fetchAdSummaryByAd,
  fetchAdSummaryBySubtheme,
  fetchAdSummaryByTheme,
  fetchAdSummaryFilterOptions,
  pauseAdSummaryAd,
} from '@src/api';
import api from '@src/api/clients';
import { formatDateToYMD } from '@src/utils/dateUtils';

const VIEW_MODE_OPTIONS = [
  { key: 'ad', label: 'Ads' },
  { key: 'theme', label: 'Themes' },
  { key: 'subtheme', label: 'Subthemes' },
];

const CLIENT_FIELDS = [
  'id',
  'name',
  'slug',
  'parent_company',
  'parent_company_name',
  'mapping_types',
].join(',');

const MAX_CLIENT_META_PAGES = 50;

const safeNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const safeDiv = (numerator, denominator) => {
  const num = safeNumber(numerator);
  const denom = safeNumber(denominator);
  if (!denom) return 0;
  return num / denom;
};

const toOptionObjects = (values = []) =>
  values.map((value) => ({ label: value, value }));

const arraysEqual = (left = [], right = []) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const RECENT_WINDOW_DAYS = 14;
const VIDEO_FORMAT_TOKENS = ['VIDEO', 'REEL', 'STORY', 'MOTION', 'ANIM'];
const STATIC_FORMAT_TOKENS = ['STATIC', 'IMAGE', 'PHOTO', 'STILL', 'SINGLE IMAGE'];
const DYNAMIC_FORMAT_TOKENS = ['DYNAMIC', 'CATALOG', 'DPA', 'ADVANTAGE+'];
const CAROUSEL_FORMAT_TOKENS = ['CAROUSEL', 'COLLECTION'];
const COM_CONFIDENCE_NOT_ELIGIBLE = 'Not Eligible';
const COM_CONFIDENCE_EARLY = 'Early CoM';
const COM_CONFIDENCE_DEVELOPING = 'Developing CoM';
const COM_CONFIDENCE_STABLE = 'Stable CoM';

async function fetchAllClientMetadata() {
  const allClients = [];
  let page = 1;

  while (page <= MAX_CLIENT_META_PAGES) {
    const response = await api.request({
      url: 'dashboard/clients/',
      method: 'GET',
      payload: {
        page,
        page_size: 250,
        ordering: 'name',
        fields: CLIENT_FIELDS,
      },
    });
    const data = response?.data;
    if (Array.isArray(data)) {
      allClients.push(...data);
      break;
    }

    const results = Array.isArray(data?.results) ? data.results : [];
    allClients.push(...results);

    if (!data?.next) {
      break;
    }
    page += 1;
  }

  return allClients;
}

const METRIC_COLUMNS = [
  { key: 'spend', label: 'Spend', type: 'currency' },
  { key: 'hookRate', label: 'Hook Rate', type: 'ratio' },
  { key: 'ctr', label: 'CTR', type: 'ratio' },
  { key: 'lpvRate', label: 'LPV Rate', type: 'ratio' },
  { key: 'thruplayRate', label: 'Thruplay Rate', type: 'ratio' },
  { key: 'holdRate25', label: 'Hold 25%', type: 'ratio' },
  { key: 'holdRate50', label: 'Hold 50%', type: 'ratio' },
  { key: 'holdRate75', label: 'Hold 75%', type: 'ratio' },
  { key: 'holdRate100', label: 'Hold 100%', type: 'ratio' },
  { key: 'leads', label: 'Leads', type: 'count' },
  { key: 'cpl', label: 'CPL', type: 'currency' },
  { key: 'selfBook', label: 'Self Book', type: 'count' },
  { key: 'cpSelfBook', label: 'CP Self Book', type: 'currency' },
  { key: 'booked', label: 'Booked', type: 'count' },
  { key: 'cpBooked', label: 'CP Booked', type: 'currency' },
  { key: 'setRate', label: 'Set Rate', type: 'ratio' },
  { key: 'scheduled', label: 'Appointments', type: 'count' },
  { key: 'cpScheduled', label: 'CP Appointment', type: 'currency' },
  { key: 'issues', label: 'Issues', type: 'count' },
  { key: 'cpIssue', label: 'CP Issue', type: 'currency' },
  { key: 'issueRate', label: 'Issue Rate', type: 'ratio' },
  { key: 'leadToIssue', label: 'Lead To Issue', type: 'ratio' },
  { key: 'demos', label: 'Demos', type: 'count' },
  { key: 'cpDemo', label: 'CP Demo', type: 'currency' },
  { key: 'demoRate', label: 'Demo Rate', type: 'ratio' },
  { key: 'grossSold', label: 'Gross Sold', type: 'count' },
  { key: 'netSold', label: 'Net Sold', type: 'count' },
  { key: 'leadToSale', label: 'Lead To Sale', type: 'ratio' },
  { key: 'netCloseRate', label: 'Net Close Rate', type: 'ratio' },
  { key: 'netAmount', label: 'Net Amount', type: 'currency' },
  { key: 'avgDeal', label: 'Avg Deal', type: 'currency' },
  { key: 'predictedCostOfMarketing', label: 'Pred. CoM', type: 'ratio' },
  { key: 'effectiveCostOfMarketing', label: 'Decision CoM', type: 'ratio' },
  { key: 'costOfMarketing', label: 'Cost of Marketing', type: 'ratio' },
  { key: 'creativeScore', label: 'Creative Score', type: 'count' },
  { key: 'businessScore', label: 'Business Score', type: 'count' },
  { key: 'overallScore', label: 'Overall Score', type: 'count' },
];

const DECISION_KPI_OPTION = {
  key: 'effectiveCostOfMarketing',
  label: 'Pred./Actual CoM',
  better: 'lower',
  benchmarkScope: 'client_vertical',
  isEligible: (row) => row.hasBackendIntegration && safeNumber(row.effectiveCostOfMarketing) > 0,
};

const computeDerivedMetrics = (base = {}) => {
  const spend = safeNumber(base.spend);
  const impressions = safeNumber(base.impressions);
  const clicks = safeNumber(base.clicks);
  const landingPageViews = safeNumber(base.landingPageViews);
  const videoPlays3s = safeNumber(base.videoPlays3s);
  const thruplays = safeNumber(base.thruplays);
  const videoP25 = safeNumber(base.videoP25);
  const videoP50 = safeNumber(base.videoP50);
  const videoP75 = safeNumber(base.videoP75);
  const videoP100 = safeNumber(base.videoP100);
  const leads = safeNumber(base.leads);
  const selfBook = safeNumber(base.selfBook);
  const matureSpend = base.matureSpend == null ? spend : safeNumber(base.matureSpend);
  const matureLeads = base.matureLeads == null ? leads : safeNumber(base.matureLeads);
  const booked = base.matureBooked == null ? safeNumber(base.booked) : safeNumber(base.matureBooked);
  const scheduled = base.matureScheduled == null ? safeNumber(base.scheduled) : safeNumber(base.matureScheduled);
  const issues = base.matureIssues == null ? safeNumber(base.issues) : safeNumber(base.matureIssues);
  const demos = base.matureDemos == null ? safeNumber(base.demos) : safeNumber(base.matureDemos);
  const grossSold = base.matureGrossSold == null ? safeNumber(base.grossSold) : safeNumber(base.matureGrossSold);
  const netSold = base.matureNetSold == null ? safeNumber(base.netSold) : safeNumber(base.matureNetSold);
  const netAmount = base.matureNetAmount == null ? safeNumber(base.netAmount) : safeNumber(base.matureNetAmount);

  return {
    spend,
    matureSpend,
    hookRate: safeDiv(videoPlays3s, impressions),
    ctr: safeDiv(clicks, impressions),
    lpvRate: safeDiv(landingPageViews, clicks),
    thruplayRate: safeDiv(thruplays, impressions),
    holdRate25: safeDiv(videoP25, videoPlays3s),
    holdRate50: safeDiv(videoP50, videoPlays3s),
    holdRate75: safeDiv(videoP75, videoPlays3s),
    holdRate100: safeDiv(videoP100, videoPlays3s),
    leads,
    cpl: safeDiv(spend, leads),
    selfBook,
    cpSelfBook: safeDiv(spend, selfBook),
    booked,
    cpBooked: safeDiv(matureSpend, booked),
    setRate: safeDiv(booked, matureLeads),
    scheduled,
    cpScheduled: safeDiv(matureSpend, scheduled),
    issues,
    cpIssue: safeDiv(matureSpend, issues),
    issueRate: safeDiv(issues, scheduled),
    leadToIssue: safeDiv(issues, matureLeads),
    demos,
    cpDemo: safeDiv(matureSpend, demos),
    demoRate: safeDiv(demos, issues),
    grossSold,
    netSold,
    leadToSale: safeDiv(netSold, matureLeads),
    netCloseRate: safeDiv(netSold, demos),
    netAmount,
    avgDeal: safeDiv(netAmount, netSold),
    costOfMarketing: safeDiv(matureSpend, netAmount),
  };
};

const renderMetricValue = (value, type) => {
  if (type === 'currency') {
    return <DollarFormatter value={value} />;
  }
  if (type === 'ratio') {
    return <PercentageFormatter value={value} />;
  }
  return safeNumber(value).toLocaleString();
};

const buildAdHierarchy = (rows = []) => {
  const clientMap = new Map();

  rows.forEach((row) => {
    const clientKey = `${row.clientId || 'unknown-client'}::${row.clientName || 'Unknown Client'}`;
    if (!clientMap.has(clientKey)) {
      clientMap.set(clientKey, {
        key: clientKey,
        clientName: row.clientName || 'Unknown Client',
        verticalName: row.verticalName || '',
        spend: 0,
        rows: [],
        campaigns: new Map(),
      });
    }

    const clientGroup = clientMap.get(clientKey);
    clientGroup.spend += safeNumber(row.spend);
    clientGroup.rows.push(row);

    const campaignLabel = row.campaign || 'Unknown Campaign';
    const campaignKey = `${clientKey}::${campaignLabel}`;
    if (!clientGroup.campaigns.has(campaignKey)) {
      clientGroup.campaigns.set(campaignKey, {
        key: campaignKey,
        campaign: campaignLabel,
        spend: 0,
        rows: [],
        adSets: new Map(),
      });
    }

    const campaignGroup = clientGroup.campaigns.get(campaignKey);
    campaignGroup.spend += safeNumber(row.spend);
    campaignGroup.rows.push(row);

    const adSetLabel = row.adSetName || 'Unknown Ad Set';
    const adSetKey = `${campaignKey}::${adSetLabel}`;
    if (!campaignGroup.adSets.has(adSetKey)) {
      campaignGroup.adSets.set(adSetKey, {
        key: adSetKey,
        adSetName: adSetLabel,
        spend: 0,
        rows: [],
      });
    }

    const adSetGroup = campaignGroup.adSets.get(adSetKey);
    adSetGroup.spend += safeNumber(row.spend);
    adSetGroup.rows.push(row);
  });

  return Array.from(clientMap.values())
    .map((clientGroup) => ({
      ...clientGroup,
      campaigns: Array.from(clientGroup.campaigns.values())
        .map((campaignGroup) => ({
          ...campaignGroup,
          adSets: Array.from(campaignGroup.adSets.values())
            .map((adSetGroup) => ({
              ...adSetGroup,
              rows: [...adSetGroup.rows].sort((left, right) => right.spend - left.spend),
            }))
            .sort((left, right) => right.spend - left.spend),
        }))
        .sort((left, right) => right.spend - left.spend),
    }))
    .sort((left, right) => right.spend - left.spend);
};

const getDimensionColumns = (viewMode) => {
  if (viewMode === 'theme') {
    return [
      { key: 'themeName', label: 'Theme', minWidth: 220 },
      { key: 'clientCount', label: 'Clients', minWidth: 100, type: 'count' },
      { key: 'confidenceLabel', label: 'Confidence', minWidth: 120 },
      { key: 'maturityLabel', label: 'Maturity', minWidth: 160 },
    ];
  }
  if (viewMode === 'subtheme') {
    return [
      { key: 'themeName', label: 'Theme', minWidth: 220 },
      { key: 'subthemeName', label: 'Subtheme', minWidth: 220 },
      { key: 'clientCount', label: 'Clients', minWidth: 100, type: 'count' },
      { key: 'confidenceLabel', label: 'Confidence', minWidth: 120 },
      { key: 'maturityLabel', label: 'Maturity', minWidth: 160 },
    ];
  }
  return [
    { key: 'adName', label: 'Ad', minWidth: 420, sticky: true, stickyLeft: 0, truncate: true, fontSize: 13 },
    { key: 'previewAction', label: 'Preview', minWidth: 110, stickyLeft: 420 },
    { key: 'activeStatusLabel', label: 'Status', minWidth: 120, stickyLeft: 530 },
    { key: 'facebookFormat', label: 'Format', minWidth: 120, stickyLeft: 650 },
    {
      key: 'stageLabel',
      label: 'Stage',
      minWidth: 150,
      helpText: 'The current evaluation stage for this ad based on the deepest KPI threshold it has reached.',
    },
    { key: 'confidenceLabel', label: 'Confidence', minWidth: 120 },
    {
      key: 'spendShareLabel',
      label: 'Spend Share',
      minWidth: 120,
      helpText: 'Share of spend within this client and vertical in the current date range.',
    },
    {
      key: 'primaryKpiLabel',
      label: 'Primary KPI',
      minWidth: 150,
      helpText: 'The most appropriate KPI to judge this ad right now given its maturity.',
    },
    {
      key: 'comConfidenceLabel',
      label: 'CoM Confidence',
      minWidth: 150,
      helpText: 'Confidence band for relying on Cost of Marketing based on sale count and deal-value stability.',
    },
    {
      key: 'predictedCostOfMarketingLabel',
      label: 'Pred. CoM',
      minWidth: 140,
      helpText: 'Projected Cost of Marketing based on the ad’s current stage and comparable funnel conversion rates.',
    },
    {
      key: 'effectiveCostOfMarketingLabel',
      label: 'Decision CoM',
      minWidth: 150,
      helpText: 'Actual Cost of Marketing for mature ads, otherwise the predicted Cost of Marketing used for cross-stage comparison.',
    },
    {
      key: 'pausePriorityLabel',
      label: 'Pause Priority',
      minWidth: 140,
      helpText: 'Higher when an ad looks weak, confidence is meaningful, and enough spend is at risk to justify action.',
    },
    {
      key: 'buildPriorityLabel',
      label: 'Build Priority',
      minWidth: 140,
      helpText: 'Higher when an ad looks like a winner worth reusing for new creative variations.',
    },
    {
      key: 'creativeVerdict',
      label: 'Creative Verdict',
      minWidth: 150,
      helpText: 'Quick read on whether the creative looks winning, promising, mixed, weak, or still too early.',
    },
    {
      key: 'overallRecommendation',
      label: 'Overall Rec.',
      minWidth: 150,
      helpText: 'Suggested next action based on the full selected date range.',
    },
    {
      key: 'whyLabel',
      label: 'Why',
      minWidth: 340,
      helpText: 'Short explanation for the current recommendation.',
      truncate: true,
      fontSize: 12.5,
    },
  ];
};

const getRowLabel = (row, viewMode) => {
  if (viewMode === 'theme') return row.themeName || 'Unparsed Theme';
  if (viewMode === 'subtheme') return row.subthemeName || row.themeName || 'Unparsed Subtheme';
  return row.adName || 'Unnamed Ad';
};

const getStatusPillStyles = (tone) => {
  const palette = {
    success: { bg: '#dcfce7', fg: '#166534' },
    warning: { bg: '#fef3c7', fg: '#92400e' },
    danger: { bg: '#fee2e2', fg: '#b91c1c' },
    info: { bg: '#dbeafe', fg: '#1d4ed8' },
    neutral: { bg: '#e5e7eb', fg: '#374151' },
    violet: { bg: '#ede9fe', fg: '#6d28d9' },
  };
  return palette[tone] || palette.neutral;
};

const renderStatusPill = (label, tone = 'neutral') => {
  const colors = getStatusPillStyles(tone);
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1.1,
        py: 0.45,
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        backgroundColor: colors.bg,
        color: colors.fg,
      }}
    >
      {label || '-'}
    </Box>
  );
};

const getRatioTone = (value) => {
  const numeric = safeNumber(value);
  if (!numeric) return 'neutral';
  if (numeric <= 0.2) return 'success';
  if (numeric <= 0.35) return 'warning';
  return 'danger';
};

const getPriorityTone = (value) => {
  const numeric = safeNumber(value);
  if (numeric >= 70) return 'danger';
  if (numeric >= 40) return 'warning';
  return 'neutral';
};

const getBuildTone = (value) => {
  const numeric = safeNumber(value);
  if (numeric >= 70) return 'success';
  if (numeric >= 40) return 'info';
  return 'neutral';
};

const getConfidenceTone = (label) => {
  if (label === 'High') return 'success';
  if (label === 'Medium') return 'warning';
  return 'neutral';
};

const getRecommendationTone = (label) => {
  if (label === 'Pause') return 'danger';
  if (label === 'Keep Running') return 'success';
  if (label === 'Test Longer') return 'warning';
  if (label === 'Watch') return 'info';
  return 'neutral';
};

const getActiveTone = (isActive) => (isActive ? 'success' : 'danger');

const getVerdictTone = (label) => {
  if (label === 'Winning') return 'success';
  if (label === 'Promising') return 'info';
  if (label === 'Weak') return 'danger';
  if (label === 'Mixed') return 'warning';
  return 'neutral';
};

const getStageTone = (label) => {
  if (label === 'Sales Stage') return 'success';
  if (label === 'Demo Stage' || label === 'Issue Stage' || label === 'Appointment Stage') return 'info';
  if (label === 'Lead Stage' || label === 'Self Book Stage') return 'warning';
  if (label === 'Creative Signal') return 'violet';
  return 'neutral';
};

const getWhyDriverTone = (directionalDelta) => {
  if (directionalDelta >= 0.05) return 'success';
  if (directionalDelta <= -0.05) return 'danger';
  return 'neutral';
};

const formatWhyDriverChip = (driver) => {
  if (!driver || driver.delta == null) return null;
  const sign = driver.directionalDelta >= 0 ? '+' : '-';
  const amount = Math.abs(safeNumber(driver.delta) * 100).toFixed(0);
  return `${driver.label} ${sign}${amount}%`;
};

const buildPauseAuditNote = (row = {}) => {
  const parts = [];
  if (row.overallRecommendation) parts.push(`Decision: ${row.overallRecommendation}.`);
  if (row.whyLabel) parts.push(row.whyLabel);
  const chips = Array.isArray(row.whyDriverChips)
    ? row.whyDriverChips.map((driver) => formatWhyDriverChip(driver)).filter(Boolean)
    : [];
  if (chips.length) parts.push(`Drivers: ${chips.join(', ')}.`);
  if (row.comConfidenceLabel) parts.push(`CoM Confidence: ${row.comConfidenceLabel}.`);
  if (row.dealValueStability) parts.push(`Deal Stability: ${row.dealValueStability}.`);
  return parts.join(' ').trim();
};

const getComConfidenceTone = (label) => {
  if (label === COM_CONFIDENCE_STABLE) return 'success';
  if (label === COM_CONFIDENCE_DEVELOPING) return 'info';
  if (label === COM_CONFIDENCE_EARLY) return 'warning';
  return 'neutral';
};

const getFormatTone = (label) => {
  const format = String(label || '').toUpperCase();
  if (VIDEO_FORMAT_TOKENS.some((token) => format.includes(token))) return 'info';
  if (!format || format === 'UNKNOWN') return 'neutral';
  return 'violet';
};

const renderDimensionValue = (column, row, onPreviewOpen) => {
  if (column.key === 'previewAction') {
    return row.thumbnailUrl || row.imageUrl || row.headline || row.primaryText ? (
      <Button size="small" variant="outlined" onClick={() => onPreviewOpen(row)}>
        Preview
      </Button>
    ) : (
      <Typography variant="body2" color="text.secondary">-</Typography>
    );
  }

  if (column.key === 'activeStatusLabel') return renderStatusPill(row[column.key] || '-', getActiveTone(row.isActive));
  if (column.key === 'facebookFormat') return renderStatusPill(row[column.key] || 'Unknown', getFormatTone(row[column.key]));
  if (column.key === 'stageLabel') return renderStatusPill(row[column.key] || '-', getStageTone(row[column.key]));
  if (column.key === 'confidenceLabel') return renderStatusPill(row[column.key] || '-', getConfidenceTone(row[column.key]));
  if (column.key === 'comConfidenceLabel') return renderStatusPill(row[column.key] || '-', getComConfidenceTone(row[column.key]));
  if (column.key === 'spendShareLabel') return renderStatusPill(row[column.key] || '-', getRatioTone(row.spendShare));
  if (column.key === 'primaryKpiLabel') return renderStatusPill(row[column.key] || '-', 'neutral');
  if (column.key === 'predictedCostOfMarketingLabel') return renderStatusPill(row[column.key] || '-', getRatioTone(row.predictedCostOfMarketing));
  if (column.key === 'effectiveCostOfMarketingLabel') return renderStatusPill(row[column.key] || '-', getRatioTone(row.effectiveCostOfMarketing));
  if (column.key === 'pausePriorityLabel') return renderStatusPill(row[column.key] || '-', getPriorityTone(row.pausePriority));
  if (column.key === 'buildPriorityLabel') return renderStatusPill(row[column.key] || '-', getBuildTone(row.buildPriority));
  if (column.key === 'creativeVerdict') return renderStatusPill(row[column.key] || '-', getVerdictTone(row[column.key]));
  if (column.key === 'overallRecommendation') return renderStatusPill(row[column.key] || '-', getRecommendationTone(row[column.key]));
  if (column.key === 'whyLabel') {
    const whyDriverChips = Array.isArray(row.whyDriverChips) ? row.whyDriverChips : [];
    return (
      <Stack spacing={0.6} sx={{ maxWidth: column.minWidth }}>
        <Typography
          noWrap
          sx={{
            maxWidth: column.minWidth,
            fontSize: column.fontSize || 'inherit',
            lineHeight: 1.25,
          }}
        >
          {row[column.key] || '-'}
        </Typography>
        {whyDriverChips.length ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {whyDriverChips.map((driver) => (
              <React.Fragment key={`${driver.key}-${driver.label}`}>
                {renderStatusPill(formatWhyDriverChip(driver), getWhyDriverTone(driver.directionalDelta))}
              </React.Fragment>
            ))}
          </Box>
        ) : null}
      </Stack>
    );
  }
  if (column.key === 'adName') {
    return (
      <Stack spacing={0.25} sx={{ maxWidth: column.minWidth }}>
        <Typography
          noWrap
          sx={{
            maxWidth: column.minWidth,
            fontSize: column.fontSize || 'inherit',
            fontWeight: 600,
            lineHeight: 1.25,
          }}
        >
          {row.adName || '-'}
        </Typography>
        {row.themeName || row.subthemeName ? (
          <Typography
            noWrap
            variant="caption"
            color="text.secondary"
            sx={{ maxWidth: column.minWidth, lineHeight: 1.2 }}
          >
            {[row.themeName, row.subthemeName].filter(Boolean).join(' • ')}
          </Typography>
        ) : null}
      </Stack>
    );
  }
  if (column.type === 'count') return safeNumber(row[column.key]).toLocaleString();
  if (column.truncate) {
    return (
      <Typography
        noWrap
        sx={{
          maxWidth: column.minWidth,
          fontSize: column.fontSize || 'inherit',
          lineHeight: 1.25,
        }}
      >
        {row[column.key] || '-'}
      </Typography>
    );
  }
  return row[column.key] || '-';
};

const getRankedRows = (rows, kpiOption, direction) => {
  if (!kpiOption) return [];

  const eligibleRows = rows
    .filter((row) => row.spend > 0)
    .filter((row) => kpiOption.isEligible(row));

  const sorted = [...eligibleRows].sort((left, right) => {
    const leftValue = safeNumber(left[kpiOption.key]);
    const rightValue = safeNumber(right[kpiOption.key]);

    if (kpiOption.better === 'lower') {
      return direction === 'best' ? leftValue - rightValue : rightValue - leftValue;
    }
    return direction === 'best' ? rightValue - leftValue : leftValue - rightValue;
  });

  return sorted.slice(0, 5);
};

const getReportFetcher = (viewMode) => {
  if (viewMode === 'theme') return fetchAdSummaryByTheme;
  if (viewMode === 'subtheme') return fetchAdSummaryBySubtheme;
  return fetchAdSummaryByAd;
};

const getAdAgeDays = (createdTime, referenceDate) => {
  if (!createdTime || !referenceDate) return null;
  const createdAt = new Date(createdTime);
  if (Number.isNaN(createdAt.getTime())) return null;
  const ref = new Date(referenceDate);
  ref.setHours(23, 59, 59, 999);
  const diffMs = ref.getTime() - createdAt.getTime();
  if (diffMs < 0) return 0;
  return Math.floor(diffMs / (1000 * 60 * 60 * 24));
};

const formatAdAge = (ageDays) => {
  if (ageDays == null) return 'Unknown';
  if (ageDays < 7) return `${ageDays}d`;
  if (ageDays < 30) return `${Math.floor(ageDays / 7)}w`;
  return `${Math.floor(ageDays / 30)}mo`;
};

const formatShortDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  });
};

const getLifecycleLabel = ({ ageDays, spend, impressions, leads, issues, demos, netSold }) => {
  if (ageDays == null) return 'Unknown';
  if (ageDays <= 14 && spend < 100 && impressions < 5000) return 'New';
  if (spend > 100 || impressions > 5000 || leads > 0 || issues > 0 || demos > 0 || netSold > 0) {
    return 'Active';
  }
  if (ageDays > 30 && spend < 25 && impressions < 1000) return 'Deprioritized';
  if (ageDays > 14) return 'Dormant';
  return 'New';
};

const KPI_STAGE_RULES = [
  {
    stageLabel: 'Sales Stage',
    thresholdLabel: '6 Net Sales',
    key: 'costOfMarketing',
    label: 'Cost of Marketing',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    qualifies: (row) => row.hasBackendIntegration && row.netSold >= 6 && row.netAmount > 0,
  },
  {
    stageLabel: 'Demo Stage',
    thresholdLabel: '6 Demos',
    key: 'cpDemo',
    label: 'Cost Per Demo',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    qualifies: (row) => row.hasBackendIntegration && row.demos >= 6,
  },
  {
    stageLabel: 'Issue Stage',
    thresholdLabel: '8 Issues',
    key: 'cpIssue',
    label: 'Cost Per Issue',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    qualifies: (row) => row.hasBackendIntegration && row.issues >= 8,
  },
  {
    stageLabel: 'Appointment Stage',
    thresholdLabel: '10 Appointments',
    key: 'cpScheduled',
    label: 'Cost Per Appointment',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    qualifies: (row) => row.hasBackendIntegration && row.scheduled >= 10,
  },
  {
    stageLabel: 'Self Book Stage',
    thresholdLabel: '10 Self Books',
    key: 'cpSelfBook',
    label: 'Cost Per Self Book',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    qualifies: (row) => row.selfBook >= 10,
  },
  {
    stageLabel: 'Lead Stage',
    thresholdLabel: '20 Leads',
    key: 'cpl',
    label: 'CPL',
    better: 'lower',
    benchmarkScope: 'client_vertical',
    qualifies: (row) => row.leads >= 20,
  },
  {
    stageLabel: 'Creative Signal',
    thresholdLabel: '10K Impressions',
    key: 'ctr',
    label: 'CTR',
    better: 'higher',
    benchmarkScope: 'agency_vertical',
    qualifies: (row) => row.impressions >= 10000,
  },
];

const getKpiStage = (row) => KPI_STAGE_RULES.find((rule) => rule.qualifies(row)) || null;

const getFunnelBenchmarks = (rows) => {
  const totals = rows.reduce(
    (acc, row) => {
      acc.leads += safeNumber(row.leads);
      acc.scheduled += safeNumber(row.scheduled);
      acc.issues += safeNumber(row.issues);
      acc.demos += safeNumber(row.demos);
      acc.netSold += safeNumber(row.netSold);
      acc.netAmount += safeNumber(row.netAmount);
      return acc;
    },
    {
      leads: 0,
      scheduled: 0,
      issues: 0,
      demos: 0,
      netSold: 0,
      netAmount: 0,
    }
  );

  return {
    leadToAppointment: safeDiv(totals.scheduled, totals.leads),
    appointmentToIssue: safeDiv(totals.issues, totals.scheduled),
    issueToDemo: safeDiv(totals.demos, totals.issues),
    demoToNetSale: safeDiv(totals.netSold, totals.demos),
    expectedAvgNetAmount: safeDiv(totals.netAmount, totals.netSold),
  };
};

const hasUsableBenchmarks = (benchmarks) => (
  safeNumber(benchmarks.leadToAppointment) > 0
  && safeNumber(benchmarks.appointmentToIssue) > 0
  && safeNumber(benchmarks.issueToDemo) > 0
  && safeNumber(benchmarks.demoToNetSale) > 0
  && safeNumber(benchmarks.expectedAvgNetAmount) > 0
);

const getPredictedCostPerNetSale = (row, benchmarks) => {
  if (!row?.hasBackendIntegration) return null;
  if (!hasUsableBenchmarks(benchmarks)) return null;

  if (row.demos >= 6 && benchmarks.demoToNetSale > 0) {
    return safeDiv(row.cpDemo, benchmarks.demoToNetSale);
  }

  if (row.issues >= 8) {
    return safeDiv(row.cpIssue, benchmarks.issueToDemo * benchmarks.demoToNetSale);
  }

  if (row.scheduled >= 10) {
    return safeDiv(
      row.cpScheduled,
      benchmarks.appointmentToIssue * benchmarks.issueToDemo * benchmarks.demoToNetSale
    );
  }

  if (row.selfBook >= 10 || row.leads >= 20) {
    return safeDiv(
      row.cpl,
      benchmarks.leadToAppointment
        * benchmarks.appointmentToIssue
        * benchmarks.issueToDemo
        * benchmarks.demoToNetSale
    );
  }

  return null;
};

const getPredictedCostOfMarketing = (row, benchmarks) => {
  const predictedCostPerNetSale = getPredictedCostPerNetSale(row, benchmarks);
  if (!predictedCostPerNetSale || !benchmarks.expectedAvgNetAmount) return null;
  return safeDiv(predictedCostPerNetSale, benchmarks.expectedAvgNetAmount);
};

const getMaturityLabel = (row) => {
  const stage = getKpiStage(row);
  if (stage) return stage.stageLabel;
  return 'Insufficient Data';
};

const getPrimaryKpi = (row) => {
  const stage = getKpiStage(row);
  if (stage) {
    return {
      key: stage.key,
      label: stage.label,
      better: stage.better,
      benchmarkScope: stage.benchmarkScope,
      isEligible: (candidate) => {
        if (stage.key === 'costOfMarketing') return candidate.hasBackendIntegration && candidate.netSold >= 6 && candidate.netAmount > 0;
        if (stage.key === 'cpDemo') return candidate.hasBackendIntegration && candidate.demos >= 6;
        if (stage.key === 'cpIssue') return candidate.hasBackendIntegration && candidate.issues >= 8;
        if (stage.key === 'cpScheduled') return candidate.hasBackendIntegration && candidate.scheduled >= 10;
        if (stage.key === 'cpSelfBook') return candidate.selfBook >= 10;
        if (stage.key === 'cpl') return candidate.leads >= 20;
        if (stage.key === 'ctr') return candidate.impressions >= 10000;
        return false;
      },
    };
  }
  return null;
};

const getBenchmarkDelta = (row, peerRows, kpi) => {
  if (!kpi || !peerRows.length) return null;
  const eligiblePeers = peerRows.filter((peer) => kpi.isEligible(peer));
  if (!eligiblePeers.length) return null;
  const peerAverage = eligiblePeers.reduce((sum, peer) => sum + safeNumber(peer[kpi.key]), 0) / eligiblePeers.length;
  if (!peerAverage) return null;
  return safeDiv(safeNumber(row[kpi.key]) - peerAverage, peerAverage);
};

const formatDeltaLabel = (delta) => {
  if (delta == null) return '-';
  const percentage = `${Math.abs(delta * 100).toFixed(0)}%`;
  return delta >= 0 ? `+${percentage}` : `-${percentage}`;
};

const formatPercentLabel = (value) => {
  if (value == null) return '-';
  return `${Math.round(safeNumber(value) * 100)}%`;
};

const formatPriorityLabel = (value) => `${clampScore(value)}/100`;

const getDirectionalDelta = (delta, better = 'higher') => {
  if (delta == null) return 0;
  return better === 'lower' ? -safeNumber(delta) : safeNumber(delta);
};

const getResolvedCreativeFormat = (row = {}) =>
  normalizeFacebookFormat(
    row.facebookFormat
    || row.facebook_format
    || row.adType
    || row.ad_type
    || ''
  );

const supportsVideoDiagnostics = (row = {}) => {
  const format = getResolvedCreativeFormat(row);
  if (format === 'IMAGE') return false;
  if (format === 'VIDEO') return true;
  return safeNumber(row.videoPlays3s) > 0 || safeNumber(row.thruplays) > 0;
};

const isVideoCreative = (row = {}) => {
  return supportsVideoDiagnostics(row);
};

const normalizeCampaignType = (value = '') => {
  const lowered = String(value).trim().toLowerCase();
  if (!lowered) return 'Unknown';
  if (lowered.includes('broad')) return 'Broad';
  if (lowered.includes('local') || lowered.includes('county')) return 'Local';
  return 'Unknown';
};

const normalizeFacebookFormat = (value = '') => {
  const normalized = String(value || '').trim();
  const upper = normalized.toUpperCase();
  if (!upper) return '';
  if (VIDEO_FORMAT_TOKENS.some((token) => upper.includes(token))) return 'VIDEO';
  if (DYNAMIC_FORMAT_TOKENS.some((token) => upper.includes(token))) return 'DYNAMIC';
  if (CAROUSEL_FORMAT_TOKENS.some((token) => upper.includes(token))) return 'CAROUSEL';
  if (STATIC_FORMAT_TOKENS.some((token) => upper.includes(token))) return 'IMAGE';
  return normalized;
};

const inferCreativeLabels = (row = {}) => {
  const explicitFormat = normalizeFacebookFormat(row.facebook_format || row.facebookFormat || '');
  const explicitAdType = String(row.ad_type || row.adType || '').trim();
  const normalizedAdType = normalizeFacebookFormat(explicitAdType);

  let facebookFormat = explicitFormat || normalizedAdType;

  if (!facebookFormat) {
    if (row.video_id || row.videoId) {
      facebookFormat = 'VIDEO';
    } else if (
      safeNumber(row.video_plays_3s_sum || row.videoPlays3s) > 0
      || safeNumber(row.thruplays_sum || row.thruplays) > 0
      || safeNumber(row.video_p25_sum || row.videoP25) > 0
    ) {
      facebookFormat = 'VIDEO';
    } else if (row.image_url || row.imageUrl || row.thumbnail_url || row.thumbnailUrl) {
      facebookFormat = 'IMAGE';
    }
  }

  let adType = explicitAdType;
  if (!adType) {
    if (facebookFormat === 'IMAGE') adType = 'Static';
    else if (facebookFormat === 'VIDEO') adType = 'Video';
    else if (facebookFormat === 'DYNAMIC') adType = 'Dynamic';
    else if (facebookFormat === 'CAROUSEL') adType = 'Carousel';
  }

  return {
    facebookFormat: facebookFormat || 'Unknown',
    adType: adType || 'Unknown',
  };
};

const getComConfidenceBand = (netSold) => {
  const sales = safeNumber(netSold);
  if (sales >= 30) return COM_CONFIDENCE_STABLE;
  if (sales >= 20) return COM_CONFIDENCE_DEVELOPING;
  if (sales >= 10) return COM_CONFIDENCE_EARLY;
  return COM_CONFIDENCE_NOT_ELIGIBLE;
};

const getComBaseActualWeight = (band) => {
  if (band === COM_CONFIDENCE_STABLE) return 1;
  if (band === COM_CONFIDENCE_DEVELOPING) return 0.7;
  if (band === COM_CONFIDENCE_EARLY) return 0.35;
  return 0;
};

const getDealStabilityLabel = (row = {}) =>
  row.dealValueStability || row.deal_value_stability || 'Unknown';

const getDealStabilityDampener = (row = {}) => {
  const provided = safeNumber(row.dealValueStabilityDampener || row.deal_value_stability_dampener);
  if (provided > 0) return Math.min(1, provided);

  const stability = getDealStabilityLabel(row);
  if (stability === 'Stable') return 1;
  if (stability === 'Mixed') return 0.85;
  if (stability === 'Volatile') return 0.65;
  return 0.8;
};

const getDecisionCostMetrics = (row, benchmarks) => {
  const predictedCostOfMarketing = getPredictedCostOfMarketing(row, benchmarks);
  const actualCostOfMarketing = row.netSold > 0 && row.costOfMarketing > 0
    ? row.costOfMarketing
    : null;
  const comConfidenceBand = getComConfidenceBand(row.netSold);
  const dealValueStability = getDealStabilityLabel(row);
  const dealValueStabilityDampener = getDealStabilityDampener(row);

  let actualWeight = getComBaseActualWeight(comConfidenceBand) * dealValueStabilityDampener;
  if (comConfidenceBand === COM_CONFIDENCE_STABLE && actualCostOfMarketing != null && dealValueStability === 'Stable') {
    actualWeight = 1;
  }
  actualWeight = Math.max(0, Math.min(1, actualWeight));

  let effectiveCostOfMarketing = null;
  if (actualCostOfMarketing != null && predictedCostOfMarketing != null) {
    effectiveCostOfMarketing = (predictedCostOfMarketing * (1 - actualWeight)) + (actualCostOfMarketing * actualWeight);
  } else if (actualCostOfMarketing != null) {
    effectiveCostOfMarketing = actualCostOfMarketing;
  } else {
    effectiveCostOfMarketing = predictedCostOfMarketing;
  }

  const decisionCostLabel = actualWeight >= 0.95
    ? 'Cost of Marketing'
    : actualWeight > 0
      ? 'Blended Cost of Marketing'
      : 'Predicted Cost of Marketing';

  return {
    predictedCostOfMarketing,
    actualCostOfMarketing,
    effectiveCostOfMarketing,
    comConfidenceBand,
    comConfidenceLabel: comConfidenceBand,
    dealValueStability,
    dealValueStabilityDampener,
    decisionCostActualWeight: actualWeight,
    decisionCostLabel,
  };
};

const getStrongestWhyDriver = (whyDrivers = [], direction = 'positive') => {
  if (direction === 'negative') {
    return whyDrivers.find((driver) => driver.directionalDelta <= -0.12) || null;
  }
  return whyDrivers.find((driver) => driver.directionalDelta >= 0.12) || null;
};

const gateRecommendationByComConfidence = ({
  recommendation,
  primaryKpi,
  comConfidenceBand,
  dealValueStability,
  confidenceScore,
  whyDrivers,
}) => {
  if (primaryKpi?.key !== 'effectiveCostOfMarketing') return recommendation;
  if (!['Keep Running', 'Pause'].includes(recommendation)) return recommendation;

  const fallback = confidenceScore < 45 ? 'Test Longer' : 'Watch';
  const positiveSupport = Boolean(getStrongestWhyDriver(whyDrivers, 'positive'));
  const negativeSupport = Boolean(getStrongestWhyDriver(whyDrivers, 'negative'));
  const alignedSupport = recommendation === 'Keep Running' ? positiveSupport : negativeSupport;
  const volatileDeals = dealValueStability === 'Volatile';

  if (comConfidenceBand === COM_CONFIDENCE_EARLY) {
    return alignedSupport && confidenceScore >= 55 && !volatileDeals ? recommendation : fallback;
  }

  if (comConfidenceBand === COM_CONFIDENCE_DEVELOPING) {
    if (volatileDeals && !alignedSupport) return 'Watch';
    return confidenceScore >= 45 ? recommendation : fallback;
  }

  if (comConfidenceBand === COM_CONFIDENCE_STABLE) {
    if (volatileDeals && confidenceScore < 60 && !alignedSupport) return 'Watch';
    return recommendation;
  }

  return fallback;
};

const getRecommendation = (row, primaryKpi, benchmarkDelta) => {
  if (!primaryKpi) return 'Test Longer';
  if (row.lifecycleLabel === 'Deprioritized') return 'Dormant';
  if (row.maturityLabel === 'Insufficient Data') return 'Test Longer';
  if (benchmarkDelta == null) return 'Watch';

  if (primaryKpi.better === 'lower') {
    if (benchmarkDelta <= -0.15) return 'Keep Running';
    if (benchmarkDelta >= 0.2) return 'Pause';
  } else {
    if (benchmarkDelta >= 0.15) return 'Keep Running';
    if (benchmarkDelta <= -0.2) return 'Pause';
  }

  if (Math.abs(benchmarkDelta) <= 0.1) return 'Keep Running';
  return 'Watch';
};

const getPeerRankLabel = (row, peerRows, kpi) => {
  if (!kpi || !peerRows.length) return '-';
  const eligiblePeers = peerRows.filter((peer) => kpi.isEligible(peer));
  if (!eligiblePeers.length) return '-';

  const comparator = kpi.better === 'lower'
    ? (peer) => safeNumber(peer[kpi.key]) >= safeNumber(row[kpi.key])
    : (peer) => safeNumber(peer[kpi.key]) <= safeNumber(row[kpi.key]);
  const beatingCount = eligiblePeers.filter(comparator).length;
  const percentile = Math.round((beatingCount / eligiblePeers.length) * 100);
  if (percentile >= 90) return 'Top 10%';
  if (percentile >= 75) return 'Top Quartile';
  if (percentile <= 25) return 'Bottom Quartile';
  return 'Middle Pack';
};

const getStabilityLabel = (recentRecommendation, overallRecommendation, trendLabel) => {
  if (recentRecommendation === 'Keep Running' && overallRecommendation === 'Keep Running') return 'Consistent Winner';
  if (recentRecommendation === 'Pause' && overallRecommendation === 'Pause') return 'Consistent Weak';
  if (recentRecommendation === 'Keep Running' && overallRecommendation !== 'Keep Running') return 'Emerging';
  if (recentRecommendation === 'Pause' && overallRecommendation !== 'Pause') return 'Fading';
  if (trendLabel === 'Improving') return 'Building';
  if (trendLabel === 'Declining') return 'Slipping';
  return 'Mixed';
};

const clampScore = (value) => Math.max(0, Math.min(100, Math.round(value)));

const scoreHigherIsBetter = (value, target, max = 1.25) =>
  clampScore((Math.min(safeDiv(value, target), max) / max) * 100);

const scoreLowerIsBetter = (value, target, max = 2) => {
  if (!value) return 0;
  const inverted = safeDiv(target, value);
  return clampScore((Math.min(inverted, max) / max) * 100);
};

const getCreativeScore = (row) => {
  const parts = [
    scoreHigherIsBetter(row.ctr, 0.0125),
    scoreHigherIsBetter(row.lpvRate, 0.45),
  ];

  if (isVideoCreative(row)) {
    parts.push(
      scoreHigherIsBetter(row.hookRate, 0.25),
      scoreHigherIsBetter(row.thruplayRate, 0.06),
      scoreHigherIsBetter(row.holdRate50, 0.3)
    );
  }

  return clampScore(parts.reduce((sum, value) => sum + value, 0) / parts.length);
};

const getBusinessScore = (row) => {
  const parts = [
    scoreLowerIsBetter(row.cpl, 200),
    scoreLowerIsBetter(row.cpSelfBook, 400),
    scoreLowerIsBetter(row.cpScheduled, 600),
    scoreLowerIsBetter(row.cpIssue, 1000),
    scoreLowerIsBetter(row.cpDemo, 2500),
    scoreLowerIsBetter(row.effectiveCostOfMarketing || row.costOfMarketing, 0.2),
  ];
  return clampScore(parts.reduce((sum, value) => sum + value, 0) / parts.length);
};

const getStageScoreWeights = (maturityLabel) => {
  if (maturityLabel === 'Insufficient Data') return { creativeWeight: 1, businessWeight: 0 };
  if (maturityLabel === 'Creative Signal') return { creativeWeight: 0.9, businessWeight: 0.1 };
  if (maturityLabel === 'Lead Stage') return { creativeWeight: 0.7, businessWeight: 0.3 };
  if (maturityLabel === 'Self Book Stage') return { creativeWeight: 0.6, businessWeight: 0.4 };
  if (maturityLabel === 'Appointment Stage') return { creativeWeight: 0.5, businessWeight: 0.5 };
  if (maturityLabel === 'Issue Stage') return { creativeWeight: 0.45, businessWeight: 0.55 };
  if (maturityLabel === 'Demo Stage') return { creativeWeight: 0.35, businessWeight: 0.65 };
  if (maturityLabel === 'Sales Stage') return { creativeWeight: 0.25, businessWeight: 0.75 };
  return { creativeWeight: 0.5, businessWeight: 0.5 };
};

const getOverallScore = (row, creativeScore, businessScore, confidenceScore = 100) => {
  const { creativeWeight, businessWeight } = getStageScoreWeights(row.maturityLabel);
  if (!businessWeight) return clampScore(creativeScore);

  const normalizedConfidence = Math.max(0, Math.min(1, safeNumber(confidenceScore) / 100));
  const businessTrust = 0.35 + (normalizedConfidence * 0.65);
  const effectiveBusinessWeight = businessWeight * businessTrust;
  const effectiveCreativeWeight = 1 - effectiveBusinessWeight;

  return clampScore((creativeScore * effectiveCreativeWeight) + (businessScore * effectiveBusinessWeight));
};

const getBenchmarkAdjustedScore = (baseScore, delta, better = 'higher') => {
  if (delta == null) return clampScore(baseScore);
  const normalizedDelta = better === 'lower' ? -safeNumber(delta) : safeNumber(delta);
  const adjustment = Math.max(-20, Math.min(20, normalizedDelta * 40));
  return clampScore(baseScore + adjustment);
};

const getConfidenceScore = (row) => {
  let score = 0;

  if (row.impressions >= 25000) score += 20;
  else if (row.impressions >= 10000) score += 16;
  else if (row.impressions >= 5000) score += 12;
  else if (row.impressions >= 1000) score += 8;
  else if (row.impressions > 0) score += 4;

  if (row.leads >= 25) score += 20;
  else if (row.leads >= 15) score += 16;
  else if (row.leads >= 10) score += 12;
  else if (row.leads >= 5) score += 8;
  else if (row.leads > 0) score += 4;

  if (row.issues >= 25) score += 20;
  else if (row.issues >= 8) score += 16;
  else if (row.issues >= 5) score += 12;
  else if (row.issues >= 2) score += 8;
  else if (row.issues > 0) score += 4;

  if (row.demos >= 25) score += 20;
  else if (row.demos >= 4) score += 16;
  else if (row.demos >= 2) score += 12;
  else if (row.demos > 0) score += 6;

  if (row.netSold >= 10) score += 20;
  else if (row.netSold >= 2) score += 16;
  else if (row.netSold >= 1) score += 10;
  else if (row.netAmount >= 5000) score += 8;

  return clampScore(score);
};

const getConfidenceLabel = (score) => {
  if (score >= 75) return 'High';
  if (score >= 45) return 'Medium';
  return 'Low';
};

const getTrendLabel = (overallDelta, recentDelta, better = 'higher') => {
  if (overallDelta == null || recentDelta == null) return 'Flat';
  const directionalRecent = better === 'lower' ? -safeNumber(recentDelta) : safeNumber(recentDelta);
  const directionalOverall = better === 'lower' ? -safeNumber(overallDelta) : safeNumber(overallDelta);
  const change = directionalRecent - directionalOverall;
  if (change >= 0.1) return 'Improving';
  if (change <= -0.1) return 'Declining';
  return 'Flat';
};

const WHY_COMPONENT_METRICS = [
  { key: 'ctr', label: 'CTR', better: 'higher', isEligible: (row) => row.impressions >= 1000 && safeNumber(row.ctr) > 0 },
  { key: 'lpvRate', label: 'LPV Rate', better: 'higher', isEligible: (row) => row.clicks >= 10 && safeNumber(row.lpvRate) > 0 },
  { key: 'hookRate', label: 'Hook Rate', better: 'higher', isEligible: (row) => supportsVideoDiagnostics(row) && safeNumber(row.videoPlays3s) > 0 && safeNumber(row.hookRate) > 0 },
  { key: 'thruplayRate', label: 'Thruplay Rate', better: 'higher', isEligible: (row) => supportsVideoDiagnostics(row) && safeNumber(row.thruplays) > 0 && safeNumber(row.thruplayRate) > 0 },
  { key: 'holdRate25', label: 'Hold 25%', better: 'higher', isEligible: (row) => supportsVideoDiagnostics(row) && safeNumber(row.videoP25) > 0 && safeNumber(row.holdRate25) > 0 },
  { key: 'holdRate50', label: 'Hold 50%', better: 'higher', isEligible: (row) => supportsVideoDiagnostics(row) && safeNumber(row.videoP50) > 0 && safeNumber(row.holdRate50) > 0 },
  { key: 'cpl', label: 'CPL', better: 'lower', isEligible: (row) => row.leads >= 3 && safeNumber(row.cpl) > 0 },
  { key: 'cpSelfBook', label: 'Cost per Self Book', better: 'lower', isEligible: (row) => row.selfBook >= 2 && safeNumber(row.cpSelfBook) > 0 },
  { key: 'setRate', label: 'Set Rate', better: 'higher', isEligible: (row) => row.leads >= 5 && safeNumber(row.setRate) > 0 },
  { key: 'cpScheduled', label: 'Cost per Appointment', better: 'lower', isEligible: (row) => row.scheduled >= 2 && safeNumber(row.cpScheduled) > 0 },
  { key: 'cpIssue', label: 'Cost per Issue', better: 'lower', isEligible: (row) => row.issues >= 2 && safeNumber(row.cpIssue) > 0 },
  { key: 'issueRate', label: 'Issue Rate', better: 'higher', isEligible: (row) => row.scheduled >= 2 && safeNumber(row.issueRate) > 0 },
  { key: 'cpDemo', label: 'Cost per Demo', better: 'lower', isEligible: (row) => row.demos >= 2 && safeNumber(row.cpDemo) > 0 },
  { key: 'demoRate', label: 'Demo Rate', better: 'higher', isEligible: (row) => row.issues >= 2 && safeNumber(row.demoRate) > 0 },
  { key: 'leadToSale', label: 'Lead to Sale', better: 'higher', isEligible: (row) => row.netSold >= 1 && safeNumber(row.leadToSale) > 0 },
  { key: 'netCloseRate', label: 'Net Close Rate', better: 'higher', isEligible: (row) => row.demos >= 2 && safeNumber(row.netCloseRate) > 0 },
];

const getWhyDrivers = (row, peerRows) => {
  if (!peerRows.length) return [];

  return WHY_COMPONENT_METRICS
    .filter((metric) => metric.isEligible(row))
    .map((metric) => {
      const delta = getBenchmarkDelta(row, peerRows, {
        key: metric.key,
        better: metric.better,
        isEligible: metric.isEligible,
      });
      const directionalDelta = getDirectionalDelta(delta, metric.better);
      return {
        ...metric,
        delta,
        directionalDelta,
      };
    })
    .filter((metric) => metric.delta != null)
    .sort((left, right) => Math.abs(right.directionalDelta) - Math.abs(left.directionalDelta));
};

const getTopWhyDriverChips = (whyDrivers = [], limit = 2) =>
  whyDrivers
    .filter((driver) => Math.abs(safeNumber(driver.directionalDelta)) >= 0.05)
    .slice(0, limit);

const formatWhyDriverComparison = (driver) => {
  if (!driver || driver.delta == null) return '';
  const amount = `${Math.abs(safeNumber(driver.delta) * 100).toFixed(0)}%`;
  return driver.directionalDelta >= 0 ? `${amount} better than peers` : `${amount} worse than peers`;
};

const getWhyLabel = ({
  row,
  primaryKpi,
  benchmarkDelta,
  benchmarkScope,
  recommendation,
  diagnosticLabel,
  maturityLabel,
  spendShare,
  confidenceLabel,
  whyDrivers,
}) => {
  if (!primaryKpi) return 'Not enough mature data yet.';
  if (recommendation === 'Test Longer') return 'Not enough mature data yet.';
  if (recommendation === 'Dormant') return 'Older ad with very low recent delivery.';
  if (!row?.hasBackendIntegration && ['Lead Stage', 'Self Book Stage'].includes(maturityLabel)) {
    return 'This client has no backend integration, so evaluation stops at lead and self-book stages.';
  }
  if (benchmarkDelta == null) return 'No valid peer benchmark in the current scope.';

  const scopeLabel = benchmarkScope === 'agency_vertical' ? 'agency vertical' : 'client vertical';
  const deltaText = `${Math.abs(safeNumber(benchmarkDelta) * 100).toFixed(0)}%`;
  const betterIsHigher = primaryKpi.better === 'higher';
  const isWinning = betterIsHigher ? benchmarkDelta >= 0 : benchmarkDelta <= 0;
  const relativeText = isWinning ? 'above' : 'below';
  const strongestDriver = whyDrivers.find((driver) => driver.directionalDelta > 0.05) || null;
  const weakestDriver = whyDrivers.find((driver) => driver.directionalDelta < -0.05) || null;

  if (strongestDriver || weakestDriver) {
    if (isWinning) {
      if (strongestDriver && weakestDriver) {
        return `${strongestDriver.label} is leading (${formatWhyDriverComparison(strongestDriver)}); biggest drag is ${weakestDriver.label.toLowerCase()} (${formatWhyDriverComparison(weakestDriver)}).`;
      }
      if (strongestDriver) {
        return `${strongestDriver.label} is the main advantage at ${formatWhyDriverComparison(strongestDriver)}.`;
      }
    } else {
      if (weakestDriver && strongestDriver) {
        return `${weakestDriver.label} is the main drag (${formatWhyDriverComparison(weakestDriver)}); best offset is ${strongestDriver.label.toLowerCase()} (${formatWhyDriverComparison(strongestDriver)}).`;
      }
      if (weakestDriver) {
        return `${weakestDriver.label} is the main drag at ${formatWhyDriverComparison(weakestDriver)}.`;
      }
    }
  }

  if (maturityLabel === 'Creative Signal' && primaryKpi.key === 'ctr') {
    if (isVideoCreative(row)) {
      return `${relativeText === 'above' ? 'Beating' : 'Lagging'} ${scopeLabel} on CTR by ${deltaText}; Hook Rate is the secondary video diagnostic.`;
    }
    return `${relativeText === 'above' ? 'Beating' : 'Lagging'} ${scopeLabel} on CTR by ${deltaText}.`;
  }
  if (primaryKpi.key === 'effectiveCostOfMarketing') {
    const spendText = safeNumber(spendShare) >= 0.1 ? ` with ${formatPercentLabel(spendShare)} of spend` : '';
    return `${relativeText === 'above' ? 'Beating' : 'Lagging'} ${scopeLabel} on projected/actual Cost of Marketing by ${deltaText}${spendText}.`;
  }
  if (diagnosticLabel === 'Funnel Problem') {
    return `Creative is engaging, but backend quality is ${relativeText === 'above' ? 'still mixed' : 'lagging'} peers.`;
  }
  if (diagnosticLabel === 'Creative Problem') {
    return `Backend outcomes are acceptable, but top-funnel creative engagement trails peers.`;
  }
  if (diagnosticLabel === 'Backend Problem') {
    return `Ads are reaching demos, but sales conversion is still weak.`;
  }
  if (recommendation === 'Pause' && confidenceLabel === 'High') {
    return `${relativeText === 'above' ? 'Beating' : 'Lagging'} ${scopeLabel} on ${primaryKpi.label} by ${deltaText} with high confidence.`;
  }
  return `${relativeText === 'above' ? 'Beating' : 'Lagging'} ${scopeLabel} on ${primaryKpi.label} by ${deltaText}.`;
};

const getCreativeVerdict = (row, creativeScore, overallScore) => {
  if (row.maturityLabel === 'Insufficient Data') return 'Needs More Data';
  if (overallScore >= 75) return 'Winning';
  if (creativeScore >= 65 || overallScore >= 60) return 'Promising';
  if (overallScore <= 40) return 'Weak';
  return 'Mixed';
};

const getDiagnosticLabel = (row, creativeScore, businessScore) => {
  if (row.maturityLabel === 'Insufficient Data') return 'Needs More Data';
  if (creativeScore >= 65 && businessScore < 45) return 'Funnel Problem';
  if (creativeScore < 45 && businessScore >= 55) return 'Creative Problem';
  if (row.netSold === 0 && row.demos > 0) return 'Backend Problem';
  if (creativeScore >= 65 && businessScore >= 65) return 'Balanced Winner';
  return 'Mixed Signals';
};

const getPausePriority = ({
  overallRecommendation,
  recentRecommendation,
  creativeVerdict,
  diagnosticLabel,
  confidenceScore,
  spendShare,
  directionalDelta,
}) => {
  let score = 0;

  if (overallRecommendation === 'Pause') score += 42;
  else if (overallRecommendation === 'Watch') score += 12;
  else if (overallRecommendation === 'Dormant') score += 8;

  if (recentRecommendation === 'Pause') score += 12;
  else if (recentRecommendation === 'Watch') score += 4;

  if (directionalDelta < 0) {
    score += Math.min(24, Math.abs(directionalDelta) * 55);
  }

  if (creativeVerdict === 'Weak') score += 10;
  if (diagnosticLabel === 'Funnel Problem' || diagnosticLabel === 'Backend Problem') score += 6;

  score += safeNumber(confidenceScore) * 0.18;
  score += Math.min(18, safeNumber(spendShare) * 120);

  if (overallRecommendation === 'Test Longer') score *= 0.45;
  if (overallRecommendation === 'Keep Running') score *= 0.15;

  return clampScore(score);
};

const getBuildPriority = ({
  overallRecommendation,
  recentRecommendation,
  creativeVerdict,
  confidenceScore,
  spendShare,
  directionalDelta,
  stageLabel,
}) => {
  let score = 0;

  if (overallRecommendation === 'Keep Running') score += 42;
  else if (overallRecommendation === 'Watch') score += 6;

  if (recentRecommendation === 'Keep Running') score += 12;
  else if (recentRecommendation === 'Watch') score += 4;

  if (directionalDelta > 0) {
    score += Math.min(24, directionalDelta * 55);
  }

  if (creativeVerdict === 'Winning') score += 12;
  else if (creativeVerdict === 'Promising') score += 8;

  score += safeNumber(confidenceScore) * 0.18;
  score += Math.min(12, safeNumber(spendShare) * 80);

  if (stageLabel === 'Creative Signal') score *= 0.8;
  if (overallRecommendation === 'Pause' || overallRecommendation === 'Dormant') score *= 0.1;

  return clampScore(score);
};

const aggregatePatternLeaders = (rows, key) => {
  const grouped = new Map();
  rows.forEach((row) => {
    const groupKey = row[key];
    if (!groupKey || groupKey === '-') return;
    if (!grouped.has(groupKey)) {
      grouped.set(groupKey, {
        name: groupKey,
        count: 0,
        spend: 0,
        creativeScore: 0,
        businessScore: 0,
        overallScore: 0,
        confidenceScore: 0,
        effectiveCostOfMarketing: 0,
        effectiveCostOfMarketingCount: 0,
        keepRunningCount: 0,
        watchCount: 0,
        testLongerCount: 0,
        pauseCount: 0,
      });
    }
    const bucket = grouped.get(groupKey);
    bucket.count += 1;
    bucket.spend += safeNumber(row.spend);
    bucket.creativeScore += safeNumber(row.creativeScore);
    bucket.businessScore += safeNumber(row.businessScore);
    bucket.overallScore += safeNumber(row.overallScore);
    bucket.confidenceScore += safeNumber(row.confidenceScore);
    if (safeNumber(row.effectiveCostOfMarketing) > 0) {
      bucket.effectiveCostOfMarketing += safeNumber(row.effectiveCostOfMarketing);
      bucket.effectiveCostOfMarketingCount += 1;
    }
    if (row.overallRecommendation === 'Keep Running') bucket.keepRunningCount += 1;
    if (row.overallRecommendation === 'Watch') bucket.watchCount += 1;
    if (row.overallRecommendation === 'Test Longer') bucket.testLongerCount += 1;
    if (row.overallRecommendation === 'Pause') bucket.pauseCount += 1;
  });

  return Array.from(grouped.values()).map((bucket) => ({
    ...bucket,
    creativeScore: clampScore(bucket.creativeScore / bucket.count),
    businessScore: clampScore(bucket.businessScore / bucket.count),
    overallScore: clampScore(bucket.overallScore / bucket.count),
    confidenceScore: clampScore(bucket.confidenceScore / bucket.count),
  })).map((bucket) => {
    const confidenceFactor = 0.45 + (safeNumber(bucket.confidenceScore) / 100) * 0.55;
    const adCountFactor = Math.min(1, safeDiv(bucket.count, 3));
    const spendFactor = Math.min(1, safeDiv(bucket.spend, 10000));
    const volumeFactor = 0.5 + ((adCountFactor * 0.6) + (spendFactor * 0.4)) * 0.5;

    return {
      ...bucket,
      confidenceLabel: getConfidenceLabel(bucket.confidenceScore),
      rankingScore: clampScore(bucket.overallScore * confidenceFactor * volumeFactor),
      effectiveCostOfMarketingAvg: bucket.effectiveCostOfMarketingCount
        ? safeDiv(bucket.effectiveCostOfMarketing, bucket.effectiveCostOfMarketingCount)
        : null,
    };
  });
};

const getBestComponentLeaders = (rows, key, limit = 5) =>
  aggregatePatternLeaders(rows, key)
    .sort((a, b) => b.rankingScore - a.rankingScore)
    .slice(0, limit);

const getPatternRecommendationSummary = (pattern = {}) => {
  const parts = [];
  if (pattern.keepRunningCount) parts.push(`${pattern.keepRunningCount} Keep`);
  if (pattern.watchCount) parts.push(`${pattern.watchCount} Watch`);
  if (pattern.testLongerCount) parts.push(`${pattern.testLongerCount} Test`);
  if (pattern.pauseCount) parts.push(`${pattern.pauseCount} Pause`);
  return parts.join(' • ') || 'No action mix yet';
};

const getRecentStartDate = (startDate, endDate) => {
  const recentStart = new Date(endDate);
  recentStart.setDate(recentStart.getDate() - (RECENT_WINDOW_DAYS - 1));
  return recentStart > startDate ? recentStart : startDate;
};

const getDateRangeDays = (startDate, endDate) => {
  if (!(startDate instanceof Date) || !(endDate instanceof Date)) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  const diff = end.getTime() - start.getTime();
  return diff >= 0 ? Math.floor(diff / (1000 * 60 * 60 * 24)) + 1 : 0;
};

const median = (values = []) => {
  const sorted = values
    .map((value) => safeNumber(value))
    .filter((value) => Number.isFinite(value))
    .sort((a, b) => a - b);
  if (!sorted.length) return 0;
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2) return sorted[middle];
  return (sorted[middle - 1] + sorted[middle]) / 2;
};

const getSpendConcentrationStats = (rows = []) => {
  const spends = rows
    .map((row) => safeNumber(row.spend))
    .filter((value) => value > 0)
    .sort((a, b) => b - a);
  const totalSpend = spends.reduce((sum, value) => sum + value, 0);
  if (!totalSpend) {
    return {
      top1Share: 0,
      top3Share: 0,
      fragmented: false,
    };
  }
  const top1Share = safeDiv(spends[0], totalSpend);
  const top3Share = safeDiv(spends.slice(0, 3).reduce((sum, value) => sum + value, 0), totalSpend);
  return {
    top1Share,
    top3Share,
    fragmented: top1Share < 0.2 && top3Share < 0.55,
  };
};

const getBackendLagModifier = (rows = []) => {
  const backendRows = rows.filter((row) => row.hasBackendIntegration);
  if (!backendRows.length) {
    return {
      lagScore: 0,
      lagged: false,
      reason: 'No backend lag modifier applied.',
    };
  }

  const medianLeads = median(backendRows.map((row) => row.leads));
  const medianIssues = median(backendRows.map((row) => row.issues));
  const medianDemos = median(backendRows.map((row) => row.demos));
  const medianNetSales = median(backendRows.map((row) => row.netSold));

  let lagScore = 0;
  if (medianLeads >= 8 && medianIssues < 2) lagScore += 1;
  if (medianIssues >= 4 && medianDemos < 1) lagScore += 1;
  if (medianDemos >= 2 && medianNetSales < 1) lagScore += 1;

  if (lagScore >= 2) {
    return {
      lagScore,
      lagged: true,
      reason: 'Backend outcomes appear to lag top-funnel volume, so a longer decision window is safer.',
    };
  }

  return {
    lagScore,
    lagged: false,
    reason: 'Backend progression does not currently suggest a longer lag-adjusted window.',
  };
};

const getRangeReadiness = (rows = []) => {
  const activeRows = rows.filter((row) => row.isActive && safeNumber(row.spend) > 0);
  const activeAdCount = activeRows.length;
  const medianLeads = median(activeRows.map((row) => row.leads));
  const medianIssues = median(activeRows.map((row) => row.issues));
  const medianDemos = median(activeRows.map((row) => row.demos));
  const medianNetSales = median(activeRows.map((row) => row.netSold));
  const spendStats = getSpendConcentrationStats(activeRows);
  const lagModifier = getBackendLagModifier(activeRows);

  const enoughUsefulVolume = (
    medianLeads >= 5
    || medianIssues >= 2
    || medianDemos >= 1
    || medianNetSales >= 1
  );

  return {
    activeAdCount,
    medianLeads,
    medianIssues,
    medianDemos,
    medianNetSales,
    enoughUsefulVolume,
    spendConcentration: spendStats,
    lagModifier,
  };
};

const getRecommendedLookbackGuidance = ({
  rows = [],
  recentRows = [],
  currentRangeDays = 0,
}) => {
  const activeRows = rows.filter((row) => row.isActive && safeNumber(row.spend) > 0);
  const recentActiveRows = recentRows.filter((row) => row.isActive && safeNumber(row.spend) > 0);
  const recentReadiness = getRangeReadiness(recentActiveRows);
  const currentReadiness = getRangeReadiness(activeRows);

  let recommendedDays = 14;
  let reason = 'Median active ads already have enough recent volume for a shorter decision window.';

  if (!recentReadiness.enoughUsefulVolume) {
    recommendedDays = 30;
    reason = 'The recent 14-day window is thin at the ad level, so a longer window is recommended.';
  }

  const fragmented = (
    currentReadiness.activeAdCount >= 8
    && currentReadiness.spendConcentration.fragmented
  );
  const stillThinAtCurrent = currentRangeDays >= 30 && !currentReadiness.enoughUsefulVolume;

  if (
    recommendedDays === 30
    && (
      stillThinAtCurrent
      || fragmented
      || currentReadiness.lagModifier.lagged
    )
  ) {
    recommendedDays = 45;
    reason = fragmented
      ? 'Spend is split across many active ads, so each ad needs a longer window to mature.'
      : currentReadiness.lagModifier.lagged
        ? currentReadiness.lagModifier.reason
        : 'A 30-day window is still thin at the ad level, so 45 days is recommended.';
  }

  const detailBits = [
    `${recentReadiness.activeAdCount || currentReadiness.activeAdCount} active ads`,
    `median leads ${recentReadiness.medianLeads.toFixed(1)}`,
    `median issues ${recentReadiness.medianIssues.toFixed(1)}`,
    `median demos ${recentReadiness.medianDemos.toFixed(1)}`,
    `median net sales ${recentReadiness.medianNetSales.toFixed(1)}`,
    `top ad spend share ${Math.round((currentReadiness.spendConcentration.top1Share || recentReadiness.spendConcentration.top1Share) * 100)}%`,
  ];

  return {
    recommendedDays,
    label: `Recommended: Last ${recommendedDays} days`,
    reason,
    detailText: detailBits.join(' • '),
  };
};

const getLastThirtyDays = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 29);
  return [start, end];
};

export default function ClientPerformanceAggregator() {
  const [defaultStart, defaultEnd] = React.useMemo(() => getLastThirtyDays(), []);
  const [startDate, setStartDate] = React.useState(defaultStart);
  const [endDate, setEndDate] = React.useState(defaultEnd);
  const [loading, setLoading] = React.useState(true);
  const [loadingClientMeta, setLoadingClientMeta] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [recentRows, setRecentRows] = React.useState([]);
  const [clientMetadata, setClientMetadata] = React.useState([]);
  const [selectedParentCompanies, setSelectedParentCompanies] = React.useState([]);
  const [selectedClients, setSelectedClients] = React.useState([]);
  const [viewMode, setViewMode] = React.useState('ad');
  const [selectedVerticals, setSelectedVerticals] = React.useState([]);
  const [selectedThemes, setSelectedThemes] = React.useState([]);
  const [selectedSubthemes, setSelectedSubthemes] = React.useState([]);
  const [selectedAdTypes, setSelectedAdTypes] = React.useState([]);
  const [selectedNotes, setSelectedNotes] = React.useState([]);
  const [showPausedAds, setShowPausedAds] = React.useState(false);
  const [backendCoverageFilter, setBackendCoverageFilter] = React.useState('all');
  const [previewRow, setPreviewRow] = React.useState(null);
  const [pauseNote, setPauseNote] = React.useState('');
  const [pauseLoading, setPauseLoading] = React.useState(false);
  const [expandedClients, setExpandedClients] = React.useState({});
  const [expandedCampaigns, setExpandedCampaigns] = React.useState({});
  const [expandedAdSets, setExpandedAdSets] = React.useState({});
  const [filterOptions, setFilterOptions] = React.useState({
    verticals: [],
    themes: [],
    subthemes: [],
    adTypes: [],
    notes: [],
  });

  React.useEffect(() => {
    let isMounted = true;

    const loadClients = async () => {
      setLoadingClientMeta(true);
      try {
        const clients = await fetchAllClientMetadata();
        if (!isMounted) return;
        const normalized = clients.map((client = {}) => ({
          id: String(client.id),
          name: client.name || '',
          slug: client.slug || '',
          parentCompanyId: client.parent_company ? String(client.parent_company) : '__none__',
          parentCompanyName: client.parent_company_name || 'No Parent Company',
          hasMapping: Array.isArray(client.mapping_types) && client.mapping_types.length > 0,
        }));
        setClientMetadata(normalized);
      } catch (err) {
        console.error('Failed to load client metadata:', err);
        if (!isMounted) return;
        setClientMetadata([]);
        setError('Unable to load client metadata.');
      } finally {
        if (isMounted) {
          setLoadingClientMeta(false);
        }
      }
    };

    loadClients();
    return () => {
      isMounted = false;
    };
  }, []);

  const clientMetadataById = React.useMemo(() => {
    const next = new Map();
    clientMetadata.forEach((client) => {
      next.set(client.id, client);
    });
    return next;
  }, [clientMetadata]);

  const eligibleClients = React.useMemo(() => {
    return clientMetadata.filter((client) => {
      if (selectedParentCompanies.length > 0 && !selectedParentCompanies.includes(client.parentCompanyId)) return false;
      return true;
    });
  }, [clientMetadata, selectedParentCompanies]);

  const parentCompanyOptions = React.useMemo(() => {
    const deduped = new Map();
    eligibleClients.forEach((client) => {
      if (!deduped.has(client.parentCompanyId)) {
        deduped.set(client.parentCompanyId, {
          id: client.parentCompanyId,
          name: client.parentCompanyName,
        });
      }
    });
    return Array.from(deduped.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [eligibleClients]);

  const clientOptions = React.useMemo(() => {
    return eligibleClients
      .map((client) => ({ id: client.id, name: client.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [eligibleClients]);

  React.useEffect(() => {
    const allowed = new Set(parentCompanyOptions.map((option) => option.id));
    setSelectedParentCompanies((prev) => {
      const next = prev.filter((id) => allowed.has(id));
      return arraysEqual(prev, next) ? prev : next;
    });
  }, [parentCompanyOptions]);

  React.useEffect(() => {
    const allowed = new Set(clientOptions.map((option) => option.id));
    setSelectedClients((prev) => {
      const next = prev.filter((id) => allowed.has(id));
      return arraysEqual(prev, next) ? prev : next;
    });
  }, [clientOptions]);

  const effectiveClientIds = React.useMemo(() => {
    const scopedClients = selectedClients.length > 0
      ? eligibleClients.filter((client) => selectedClients.includes(client.id))
      : eligibleClients;

    return scopedClients.map((client) => client.id);
  }, [eligibleClients, selectedClients]);

  const mapSummaryRows = React.useCallback((summaryRows, referenceDate, currentViewMode) => (
    summaryRows
      .map((row = {}) => {
        const clientId = row.client_id ? String(row.client_id) : '';
        const clientMeta = clientId ? clientMetadataById.get(clientId) : null;
        const creativeLabels = inferCreativeLabels(row);
        const groupedHasBackendIntegration = safeNumber(row.backend_client_count) > 0;
        const hasBackendIntegration = Boolean(clientMeta?.hasMapping) || groupedHasBackendIntegration;

        const baseRow = {
          uuid: row.uuid || `${currentViewMode}-${row.theme || ''}-${row.subtheme || ''}-${row.ad_id || row.ad_name || row.client_id || ''}`,
          clientId,
          clientName: clientMeta?.name || row.client_name || row.client__name || '',
          parentCompanyId: clientMeta?.parentCompanyId || '__none__',
          parentCompanyName: clientMeta?.parentCompanyName || 'No Parent Company',
          hasMapping: hasBackendIntegration,
          hasBackendIntegration,
          verticalName: row.vertical || '',
          campaignType: normalizeCampaignType(row.campaign_type || ''),
          campaign: row.campaign || '',
          campaignId: row.campaign_id || '',
          themeName: row.theme || '',
          subthemeName: row.subtheme || '',
          adSetName: row.ad_set_name || '',
          adSetId: row.ad_set_id || '',
          adType: creativeLabels.adType,
          adName: row.ad_name || row.ad || '',
          adId: row.ad_id || '',
          notes: row.notes || '',
          isActive: row.active == null ? true : Boolean(row.active),
          activeStatusLabel: row.active == null || row.active ? 'Active' : 'Paused',
          facebookFormat: creativeLabels.facebookFormat,
          headline: row.headline || '',
          primaryText: row.primary_text || '',
          description: row.description || '',
          destinationUrl: row.destination_url || '',
          ctaType: row.cta_type || '',
          thumbnailUrl: row.thumbnail_url || '',
          imageUrl: row.image_url || '',
          videoId: row.video_id || '',
          clientCount: safeNumber(row.client_count),
          backendClientCount: safeNumber(row.backend_client_count),
          matureEndDate: row.mature_end_date || null,
          matureSpend: safeNumber(row.mature_spend_sum),
          matureLeads: safeNumber(row.mature_leads_sum),
          matureSelfBook: safeNumber(row.mature_self_books_sum),
          matureBooked: safeNumber(row.mature_booked_sum),
          matureScheduled: safeNumber(row.mature_scheduled_sum),
          matureIssues: safeNumber(row.mature_issues_sum),
          matureDemos: safeNumber(row.mature_demos_sum),
          matureGrossSold: safeNumber(row.mature_gross_sales_sum),
          matureNetSold: safeNumber(row.mature_net_sales_sum),
          matureNetAmount: safeNumber(row.mature_net_amount_sum),
          spend: safeNumber(row.total_spend_sum),
          impressions: safeNumber(row.impressions_sum),
          clicks: safeNumber(row.clicks_sum),
          landingPageViews: safeNumber(row.landing_page_views_sum),
          videoPlays3s: safeNumber(row.video_plays_3s_sum),
          thruplays: safeNumber(row.thruplays_sum),
          videoP25: safeNumber(row.video_p25_sum),
          videoP50: safeNumber(row.video_p50_sum),
          videoP75: safeNumber(row.video_p75_sum),
          videoP100: safeNumber(row.video_p100_sum),
          leads: safeNumber(row.total_leads_sum),
          selfBook: safeNumber(row.self_books_sum),
          booked: safeNumber(row.apps_booked_sum || row.booked_sum),
          scheduled: safeNumber(row.scheduled_sum),
          issues: safeNumber(row.issue_sum || row.issues_sum),
          demos: safeNumber(row.demo_sum || row.demos_sum),
          grossSold: safeNumber(row.gross_sold_sum || row.gross_sales_sum),
          netSold: safeNumber(row.net_sold_sum || row.net_sales_sum),
          netAmount: safeNumber(row.net_total_sum || row.net_amount_sum),
          saleCount: safeNumber(row.sale_count),
          avgDealValue: safeNumber(row.avg_deal_value),
          dealValueStddev: safeNumber(row.deal_value_stddev),
          dealValueCv: row.deal_value_cv == null ? null : safeNumber(row.deal_value_cv),
          dealValueStability: row.deal_value_stability || 'Unknown',
          dealValueStabilityDampener: safeNumber(row.deal_value_stability_dampener) || 0.8,
          adCreatedTime: row.ad_created_time || null,
        };

        const derived = computeDerivedMetrics(baseRow);
        const maturityLabel = getMaturityLabel({
          ...baseRow,
          ...derived,
        });
        const confidenceScore = getConfidenceScore({
          ...baseRow,
          ...derived,
        });
        const adAgeDays = getAdAgeDays(baseRow.adCreatedTime, referenceDate);
        const creativeScore = getCreativeScore({
          ...baseRow,
          ...derived,
        });
        const businessScore = getBusinessScore({
          ...baseRow,
          ...derived,
        });
        const overallScore = getOverallScore(
          { ...baseRow, ...derived, maturityLabel },
          creativeScore,
          businessScore,
          confidenceScore
        );

        return {
          ...baseRow,
          ...derived,
          adAgeDays,
          adAgeLabel: formatAdAge(adAgeDays),
          creativeScore,
          businessScore,
          overallScore,
          confidenceScore,
          lifecycleLabel: getLifecycleLabel({
            ageDays: adAgeDays,
            spend: baseRow.spend,
            impressions: baseRow.impressions,
            leads: baseRow.leads,
            issues: baseRow.issues,
            demos: baseRow.demos,
            netSold: baseRow.netSold,
          }),
          maturityLabel,
          creativeVerdict: getCreativeVerdict(
            { ...baseRow, ...derived, maturityLabel },
            creativeScore,
            overallScore
          ),
          diagnosticLabel: getDiagnosticLabel(
            { ...baseRow, ...derived, maturityLabel },
            creativeScore,
            businessScore
          ),
        };
      })
      .sort((left, right) => right.spend - left.spend)
  ), [clientMetadataById]);

  React.useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (loadingClientMeta) return;

      setLoading(true);
      setError(null);

      try {
        const payload = {
          date_after: formatDateToYMD(startDate),
          date_before: formatDateToYMD(endDate),
          ordering: '-total_spend_sum',
          campaign_type: 'Broad',
          ...(selectedParentCompanies.length ? { parent_company_ids: selectedParentCompanies.join(',') } : {}),
          ...(effectiveClientIds.length ? { client_ids: effectiveClientIds.join(',') } : {}),
          ...(selectedVerticals.length ? { vertical: selectedVerticals.join(',') } : {}),
          ...(selectedThemes.length ? { theme: selectedThemes.join(',') } : {}),
          ...(selectedSubthemes.length ? { subtheme: selectedSubthemes.join(',') } : {}),
          ...(selectedAdTypes.length ? { ad_type: selectedAdTypes.join(',') } : {}),
          ...(selectedNotes.length ? { notes: selectedNotes.join(',') } : {}),
        };

        const recentStart = getRecentStartDate(startDate, endDate);
        const recentPayload = {
          ...payload,
          date_after: formatDateToYMD(recentStart),
          date_before: formatDateToYMD(endDate),
        };

        const [summaryResp, recentSummaryResp] = await Promise.all([
          getReportFetcher(viewMode)(payload),
          getReportFetcher(viewMode)(recentPayload),
        ]);

        const summaryRows = Array.isArray(summaryResp?.data)
          ? summaryResp.data
          : Array.isArray(summaryResp?.data?.results)
            ? summaryResp.data.results
            : [];
        const recentSummaryRows = Array.isArray(recentSummaryResp?.data)
          ? recentSummaryResp.data
          : Array.isArray(recentSummaryResp?.data?.results)
            ? recentSummaryResp.data.results
            : [];

        const nextRows = mapSummaryRows(summaryRows, endDate, viewMode);
        const nextRecentRows = mapSummaryRows(recentSummaryRows, endDate, viewMode);

        if (isMounted) {
          setRows(nextRows);
          setRecentRows(nextRecentRows);
        }
      } catch (err) {
        console.error('Failed to load ad performance aggregate:', err);
        if (isMounted) {
          setRows([]);
          setRecentRows([]);
          setError('Unable to load aggregated ad performance data.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [
    clientMetadataById,
    effectiveClientIds,
    endDate,
    loadingClientMeta,
    selectedAdTypes,
    selectedNotes,
    selectedParentCompanies,
    selectedSubthemes,
    selectedThemes,
    selectedVerticals,
    startDate,
    viewMode,
    mapSummaryRows,
  ]);

  React.useEffect(() => {
    let isMounted = true;

    const loadFilterOptions = async () => {
      if (loadingClientMeta) return;

      try {
        const payload = {
          date_after: formatDateToYMD(startDate),
          date_before: formatDateToYMD(endDate),
          ...(selectedParentCompanies.length ? { parent_company_ids: selectedParentCompanies.join(',') } : {}),
          ...(effectiveClientIds.length ? { client_ids: effectiveClientIds.join(',') } : {}),
        };
        const response = await fetchAdSummaryFilterOptions(payload);
        const data = response?.data || {};
        if (!isMounted) return;
        setFilterOptions({
          verticals: toOptionObjects(data.verticals || []),
          themes: toOptionObjects(data.themes || []),
          subthemes: toOptionObjects(data.subthemes || []),
          adTypes: toOptionObjects(data.ad_types || []),
          notes: toOptionObjects(data.notes || []),
        });
      } catch (err) {
        console.error('Failed to load ad summary filter options:', err);
        if (!isMounted) return;
        setFilterOptions({
          verticals: [],
          themes: [],
          subthemes: [],
          adTypes: [],
          notes: [],
        });
      }
    };

    loadFilterOptions();
    return () => {
      isMounted = false;
    };
  }, [effectiveClientIds, endDate, loadingClientMeta, selectedParentCompanies, startDate]);

  const filteredRows = React.useMemo(
    () =>
      rows.filter((row) => (
        (backendCoverageFilter !== 'backend_only' || row.hasBackendIntegration)
        && (viewMode !== 'ad' || showPausedAds || row.isActive)
      )),
    [backendCoverageFilter, rows, showPausedAds, viewMode]
  );

  const filteredRecentRows = React.useMemo(
    () =>
      recentRows.filter((row) => (
        (backendCoverageFilter !== 'backend_only' || row.hasBackendIntegration)
      )),
    [backendCoverageFilter, recentRows]
  );

  const decisionRows = React.useMemo(() => {
    if (viewMode !== 'ad') {
      return filteredRows.map((row) => {
        const confidenceScore = getConfidenceScore(row);
        return {
          ...row,
          confidenceScore,
          confidenceLabel: getConfidenceLabel(confidenceScore),
          spendShareLabel: '-',
          peerRankLabel: '-',
          stabilityLabel: '-',
          trendLabel: '-',
          whyLabel: 'Grouped summary row.',
        };
      });
    }

    const recentRowsByUuid = new Map(filteredRecentRows.map((row) => [row.uuid, row]));

    return filteredRows.map((row) => {
      const primaryKpi = getPrimaryKpi(row);
      const recentRow = recentRowsByUuid.get(row.uuid) || row;
      const recentPrimaryKpi = getPrimaryKpi(recentRow);
      const clientVerticalPeers = filteredRows.filter(
        (peer) => (
          peer.clientId === row.clientId
          && peer.verticalName === row.verticalName
          && peer.campaignType === row.campaignType
          && peer.uuid !== row.uuid
        )
      );
      const agencyVerticalPeers = filteredRows.filter(
        (peer) => (
          peer.verticalName === row.verticalName
          && peer.campaignType === row.campaignType
          && peer.uuid !== row.uuid
        )
      );
      const backendClientVerticalPeers = clientVerticalPeers.filter((peer) => peer.hasBackendIntegration);
      const backendAgencyVerticalPeers = agencyVerticalPeers.filter((peer) => peer.hasBackendIntegration);
      const clientBenchmarks = getFunnelBenchmarks(backendClientVerticalPeers);
      const agencyBenchmarks = getFunnelBenchmarks(backendAgencyVerticalPeers);
      const selectedBenchmarks = hasUsableBenchmarks(clientBenchmarks)
        ? clientBenchmarks
        : agencyBenchmarks;
      const decisionCostMetrics = getDecisionCostMetrics(row, selectedBenchmarks);
      const {
        predictedCostOfMarketing,
        effectiveCostOfMarketing,
        comConfidenceBand,
        comConfidenceLabel,
        dealValueStability,
        dealValueStabilityDampener,
        decisionCostActualWeight,
        decisionCostLabel,
      } = decisionCostMetrics;
      const decisionCostKpi = effectiveCostOfMarketing > 0
        ? {
            key: 'effectiveCostOfMarketing',
            label: decisionCostLabel,
            better: 'lower',
            benchmarkScope: 'client_vertical',
            isEligible: (candidate) => safeNumber(candidate.effectiveCostOfMarketing) > 0,
          }
        : primaryKpi;
      const enrichedClientVerticalPeers = clientVerticalPeers.map((peer) => ({
        ...peer,
        ...getDecisionCostMetrics(peer, selectedBenchmarks),
      }));
      const enrichedAgencyVerticalPeers = agencyVerticalPeers.map((peer) => ({
        ...peer,
        ...getDecisionCostMetrics(peer, selectedBenchmarks),
      }));
      const clientVerticalDelta = getBenchmarkDelta(
        {
          ...row,
          effectiveCostOfMarketing,
        },
        enrichedClientVerticalPeers,
        decisionCostKpi
      );
      const agencyVerticalDelta = getBenchmarkDelta(
        {
          ...row,
          effectiveCostOfMarketing,
        },
        enrichedAgencyVerticalPeers,
        decisionCostKpi
      );
      const benchmarkDelta = decisionCostKpi?.benchmarkScope === 'agency_vertical'
        ? agencyVerticalDelta
        : clientVerticalDelta;

      const recentClientVerticalPeers = filteredRecentRows.filter(
        (peer) => (
          peer.clientId === recentRow.clientId
          && peer.verticalName === recentRow.verticalName
          && peer.campaignType === row.campaignType
          && peer.uuid !== recentRow.uuid
        )
      );
      const recentAgencyVerticalPeers = filteredRecentRows.filter(
        (peer) => (
          peer.verticalName === recentRow.verticalName
          && peer.campaignType === row.campaignType
          && peer.uuid !== recentRow.uuid
        )
      );
      const recentBackendClientVerticalPeers = recentClientVerticalPeers.filter((peer) => peer.hasBackendIntegration);
      const recentBackendAgencyVerticalPeers = recentAgencyVerticalPeers.filter((peer) => peer.hasBackendIntegration);
      const recentClientBenchmarks = getFunnelBenchmarks(recentBackendClientVerticalPeers);
      const recentAgencyBenchmarks = getFunnelBenchmarks(recentBackendAgencyVerticalPeers);
      const recentSelectedBenchmarks = hasUsableBenchmarks(recentClientBenchmarks)
        ? recentClientBenchmarks
        : recentAgencyBenchmarks;
      const recentDecisionCostMetrics = getDecisionCostMetrics(recentRow, recentSelectedBenchmarks);
      const recentEffectiveCostOfMarketing = recentDecisionCostMetrics.effectiveCostOfMarketing;
      const recentDecisionCostKpi = recentEffectiveCostOfMarketing > 0
        ? {
            key: 'effectiveCostOfMarketing',
            label: recentDecisionCostMetrics.decisionCostLabel,
            better: 'lower',
            benchmarkScope: 'client_vertical',
            isEligible: (candidate) => safeNumber(candidate.effectiveCostOfMarketing) > 0,
          }
        : recentPrimaryKpi;
      const enrichedRecentClientVerticalPeers = recentClientVerticalPeers.map((peer) => ({
        ...peer,
        ...getDecisionCostMetrics(peer, recentSelectedBenchmarks),
      }));
      const enrichedRecentAgencyVerticalPeers = recentAgencyVerticalPeers.map((peer) => ({
        ...peer,
        ...getDecisionCostMetrics(peer, recentSelectedBenchmarks),
      }));
      const recentClientVerticalDelta = getBenchmarkDelta(
        {
          ...recentRow,
          effectiveCostOfMarketing: recentEffectiveCostOfMarketing,
        },
        enrichedRecentClientVerticalPeers,
        recentDecisionCostKpi
      );
      const recentAgencyVerticalDelta = getBenchmarkDelta(
        {
          ...recentRow,
          effectiveCostOfMarketing: recentEffectiveCostOfMarketing,
        },
        enrichedRecentAgencyVerticalPeers,
        recentDecisionCostKpi
      );
      const recentBenchmarkDelta = recentDecisionCostKpi?.benchmarkScope === 'agency_vertical'
        ? recentAgencyVerticalDelta
        : recentClientVerticalDelta;

      const clientVerticalTotalSpend = filteredRows
        .filter((peer) => (
          peer.clientId === row.clientId
          && peer.verticalName === row.verticalName
          && peer.campaignType === row.campaignType
        ))
        .reduce((sum, peer) => sum + safeNumber(peer.spend), 0);
      const spendShare = safeDiv(row.spend, clientVerticalTotalSpend);

      const confidenceScore = getConfidenceScore(row);
      const confidenceLabel = getConfidenceLabel(confidenceScore);
      const trendLabel = getTrendLabel(
        benchmarkDelta,
        recentBenchmarkDelta,
        decisionCostKpi?.better || primaryKpi?.better || 'higher'
      );

      const creativeBenchmarkDelta = getBenchmarkDelta(
        row,
        agencyVerticalPeers,
        {
          key: 'ctr',
          label: 'CTR',
          better: 'higher',
          benchmarkScope: 'agency_vertical',
          isEligible: (candidate) => candidate.impressions > 0,
        }
      );
      const businessBenchmarkDelta = primaryKpi?.benchmarkScope === 'agency_vertical'
        ? agencyVerticalDelta
        : clientVerticalDelta;

      const creativeScore = getBenchmarkAdjustedScore(row.creativeScore, creativeBenchmarkDelta, 'higher');
      const businessScore = getBenchmarkAdjustedScore(
        getBusinessScore({
          ...row,
          effectiveCostOfMarketing,
        }),
        businessBenchmarkDelta,
        decisionCostKpi?.better || primaryKpi?.better || 'higher'
      );
      const overallScore = getOverallScore(
        { ...row, maturityLabel: row.maturityLabel },
        creativeScore,
        businessScore,
        confidenceScore
      );
      const creativeVerdict = getCreativeVerdict(
        { ...row, creativeScore, businessScore, overallScore },
        creativeScore,
        overallScore
      );
      const diagnosticLabel = getDiagnosticLabel(
        { ...row, creativeScore, businessScore, overallScore },
        creativeScore,
        businessScore
      );

      const benchmarkScope = decisionCostKpi?.benchmarkScope === 'agency_vertical'
        ? 'agency_vertical'
        : 'client_vertical';
      const peerRowsForRank = benchmarkScope === 'agency_vertical'
        ? enrichedAgencyVerticalPeers
        : enrichedClientVerticalPeers;
      const whyDrivers = getWhyDrivers(
        {
          ...row,
          effectiveCostOfMarketing,
        },
        peerRowsForRank
      );
      const baseRecommendation = getRecommendation(
        {
          ...row,
          effectiveCostOfMarketing,
        },
        decisionCostKpi,
        benchmarkDelta
      );
      const overallRecommendation = gateRecommendationByComConfidence({
        recommendation: baseRecommendation,
        primaryKpi: decisionCostKpi,
        comConfidenceBand,
        dealValueStability,
        confidenceScore,
        whyDrivers,
      });
      const whyLabel = getWhyLabel({
        row,
        primaryKpi: decisionCostKpi || primaryKpi,
        benchmarkDelta,
        benchmarkScope,
        recommendation: overallRecommendation,
        diagnosticLabel,
        maturityLabel: row.maturityLabel,
        spendShare,
        confidenceLabel,
        whyDrivers,
      });
      const whyDriverChips = getTopWhyDriverChips(whyDrivers);
      const recentBaseRecommendation = getRecommendation(
        {
          ...recentRow,
          effectiveCostOfMarketing: recentEffectiveCostOfMarketing,
        },
        recentDecisionCostKpi || recentPrimaryKpi,
        recentBenchmarkDelta
      );
      const recentRecommendation = gateRecommendationByComConfidence({
        recommendation: recentBaseRecommendation,
        primaryKpi: recentDecisionCostKpi || recentPrimaryKpi,
        comConfidenceBand: recentDecisionCostMetrics.comConfidenceBand,
        dealValueStability: recentDecisionCostMetrics.dealValueStability,
        confidenceScore,
        whyDrivers,
      });
      const peerRankLabel = getPeerRankLabel(
        {
          ...row,
          effectiveCostOfMarketing,
        },
        peerRowsForRank,
        decisionCostKpi || primaryKpi
      );
      const stabilityLabel = getStabilityLabel(recentRecommendation, overallRecommendation, trendLabel);
      const directionalDelta = getDirectionalDelta(
        benchmarkDelta,
        decisionCostKpi?.better || primaryKpi?.better || 'higher'
      );
      const pausePriority = getPausePriority({
        overallRecommendation,
        recentRecommendation,
        creativeVerdict,
        diagnosticLabel,
        confidenceScore,
        spendShare,
        directionalDelta,
      });
      const buildPriority = getBuildPriority({
        overallRecommendation,
        recentRecommendation,
        creativeVerdict,
        confidenceScore,
        spendShare,
        directionalDelta,
        stageLabel: row.maturityLabel,
      });

      return {
        ...row,
        stageLabel: row.maturityLabel,
        creativeScore,
        businessScore,
        overallScore,
        creativeVerdict,
        diagnosticLabel,
        predictedCostOfMarketing,
        predictedCostOfMarketingLabel: predictedCostOfMarketing == null ? '-' : formatPercentLabel(predictedCostOfMarketing),
        effectiveCostOfMarketing,
        effectiveCostOfMarketingLabel: effectiveCostOfMarketing == null ? '-' : formatPercentLabel(effectiveCostOfMarketing),
        comConfidenceBand,
        comConfidenceLabel,
        dealValueStability,
        dealValueStabilityDampener,
        decisionCostActualWeight,
        confidenceScore,
        confidenceLabel,
        spendShare,
        spendShareLabel: formatPercentLabel(spendShare),
        pausePriority,
        pausePriorityLabel: formatPriorityLabel(pausePriority),
        buildPriority,
        buildPriorityLabel: formatPriorityLabel(buildPriority),
        peerRankLabel,
        stabilityLabel,
        primaryKpiLabel: primaryKpi?.label || '-',
        clientVerticalDelta,
        agencyVerticalDelta,
        clientVerticalDeltaLabel: formatDeltaLabel(clientVerticalDelta),
        agencyVerticalDeltaLabel: formatDeltaLabel(agencyVerticalDelta),
        decisionBenchmarkDelta: benchmarkDelta,
        recentRecommendation,
        overallRecommendation,
        trendLabel,
        whyLabel,
        whyDriverChips,
      };
    });
  }, [filteredRecentRows, filteredRows, viewMode]);

  const totals = React.useMemo(() => {
    const base = decisionRows.reduce(
      (acc, row) => {
        acc.spend += safeNumber(row.spend);
        acc.leads += safeNumber(row.leads);
        acc.selfBook += safeNumber(row.selfBook);
        acc.booked += safeNumber(row.booked);
        acc.scheduled += safeNumber(row.scheduled);
        acc.issues += safeNumber(row.issues);
        acc.demos += safeNumber(row.demos);
        acc.grossSold += safeNumber(row.grossSold);
        acc.netSold += safeNumber(row.netSold);
        acc.netAmount += safeNumber(row.netAmount);
        return acc;
      },
      {
        spend: 0,
        leads: 0,
        selfBook: 0,
        booked: 0,
        scheduled: 0,
        issues: 0,
        demos: 0,
        grossSold: 0,
        netSold: 0,
        netAmount: 0,
      }
    );
    return computeDerivedMetrics(base);
  }, [decisionRows]);

  const adHierarchy = React.useMemo(
    () => (viewMode === 'ad' ? buildAdHierarchy(decisionRows) : []),
    [decisionRows, viewMode]
  );

  React.useEffect(() => {
    if (viewMode !== 'ad') return;

    setExpandedClients((prev) => {
      const next = {};
      adHierarchy.forEach((clientGroup) => {
        next[clientGroup.key] = prev[clientGroup.key] ?? true;
      });
      const unchanged = Object.keys(next).length === Object.keys(prev).length
        && Object.keys(next).every((key) => prev[key] === next[key]);
      return unchanged ? prev : next;
    });

    setExpandedCampaigns((prev) => {
      const next = {};
      adHierarchy.forEach((clientGroup) => {
        clientGroup.campaigns.forEach((campaignGroup) => {
          next[campaignGroup.key] = prev[campaignGroup.key] ?? true;
        });
      });
      const unchanged = Object.keys(next).length === Object.keys(prev).length
        && Object.keys(next).every((key) => prev[key] === next[key]);
      return unchanged ? prev : next;
    });

    setExpandedAdSets((prev) => {
      const next = {};
      adHierarchy.forEach((clientGroup) => {
        clientGroup.campaigns.forEach((campaignGroup) => {
          campaignGroup.adSets.forEach((adSetGroup) => {
            next[adSetGroup.key] = prev[adSetGroup.key] ?? true;
          });
        });
      });
      const unchanged = Object.keys(next).length === Object.keys(prev).length
        && Object.keys(next).every((key) => prev[key] === next[key]);
      return unchanged ? prev : next;
    });
  }, [adHierarchy, viewMode]);

  const topPerformers = React.useMemo(
    () => getRankedRows(decisionRows, DECISION_KPI_OPTION, 'best'),
    [decisionRows]
  );

  const bottomPerformers = React.useMemo(
    () => getRankedRows(decisionRows, DECISION_KPI_OPTION, 'worst'),
    [decisionRows]
  );

  const bestThemes = React.useMemo(
    () => aggregatePatternLeaders(decisionRows, 'themeName').sort((a, b) => b.rankingScore - a.rankingScore).slice(0, 5),
    [decisionRows]
  );

  const worstThemes = React.useMemo(
    () => aggregatePatternLeaders(decisionRows, 'themeName').sort((a, b) => a.rankingScore - b.rankingScore).slice(0, 5),
    [decisionRows]
  );

  const bestSubthemes = React.useMemo(
    () => aggregatePatternLeaders(decisionRows, 'subthemeName').sort((a, b) => b.rankingScore - a.rankingScore).slice(0, 5),
    [decisionRows]
  );

  const worstSubthemes = React.useMemo(
    () => aggregatePatternLeaders(decisionRows, 'subthemeName').sort((a, b) => a.rankingScore - b.rankingScore).slice(0, 5),
    [decisionRows]
  );

  const bestFormats = React.useMemo(
    () => getBestComponentLeaders(decisionRows, 'facebookFormat'),
    [decisionRows]
  );

  const actionQueues = React.useMemo(() => {
    if (viewMode !== 'ad') {
      return {
        topKeepRunning: [],
        topPause: [],
        keepTesting: [],
        buildMore: [],
      };
    }
    const rankedByPause = [...decisionRows].sort((a, b) => (
      b.pausePriority - a.pausePriority || b.spend - a.spend
    ));
    const rankedByBuild = [...decisionRows].sort((a, b) => (
      b.buildPriority - a.buildPriority || b.spend - a.spend
    ));
    const buildMore = rankedByBuild
      .filter((row) => row.buildPriority >= 45 && row.creativeVerdict !== 'Weak')
      .slice(0, 5);

    return {
      topKeepRunning: rankedByBuild
        .filter((row) => row.overallRecommendation === 'Keep Running' && row.buildPriority >= 35)
        .slice(0, 5),
      topPause: rankedByPause
        .filter((row) => row.pausePriority >= 35)
        .slice(0, 5),
      keepTesting: rankedByBuild
        .filter((row) => row.overallRecommendation === 'Test Longer' || row.confidenceLabel === 'Low')
        .slice(0, 5),
      buildMore,
    };
  }, [decisionRows, viewMode]);

  const dimensionColumns = React.useMemo(() => getDimensionColumns(viewMode), [viewMode]);

  const handleDateRangeChange = (event) => {
    const selection = event?.selection || {};
    if (selection.startDate) setStartDate(selection.startDate);
    if (selection.endDate) setEndDate(selection.endDate);
  };

  const handleOpenPreview = React.useCallback((row) => {
    setPreviewRow(row);
    setPauseNote(buildPauseAuditNote(row));
  }, []);

  const handleClosePreview = React.useCallback(() => {
    setPreviewRow(null);
    setPauseNote('');
    setPauseLoading(false);
  }, []);

  const toggleClientExpanded = React.useCallback((key) => {
    setExpandedClients((prev) => ({
      ...prev,
      [key]: !(prev[key] ?? true),
    }));
  }, []);

  const toggleCampaignExpanded = React.useCallback((key) => {
    setExpandedCampaigns((prev) => ({
      ...prev,
      [key]: !(prev[key] ?? true),
    }));
  }, []);

  const toggleAdSetExpanded = React.useCallback((key) => {
    setExpandedAdSets((prev) => ({
      ...prev,
      [key]: !(prev[key] ?? true),
    }));
  }, []);

  const handlePauseAd = React.useCallback(async () => {
    if (!previewRow?.clientId || !previewRow?.adId || pauseLoading) return;

    setPauseLoading(true);
    setError(null);
    try {
      await pauseAdSummaryAd({
        client_id: previewRow.clientId,
        ad_id: previewRow.adId,
        final_note: pauseNote || buildPauseAuditNote(previewRow),
        row_snapshot: previewRow,
      });

      const applyPausedState = (currentRows) => currentRows.map((row) => {
        if (row.clientId !== previewRow.clientId || row.adId !== previewRow.adId) {
          return row;
        }
        return {
          ...row,
          isActive: false,
          activeStatusLabel: 'Paused',
        };
      });

      setRows((prev) => applyPausedState(prev));
      setRecentRows((prev) => applyPausedState(prev));
      setPreviewRow((prev) => (
        prev
          ? {
              ...prev,
              isActive: false,
              activeStatusLabel: 'Paused',
            }
          : prev
      ));
    } catch (err) {
      console.error('Failed to pause Meta ad:', err);
      const responseData = err?.response?.data;
      const detail = responseData?.detail
        || responseData?.ad_id
        || responseData?.client_id
        || 'Unable to pause the selected Meta ad.';
      setError(Array.isArray(detail) ? detail.join(' ') : String(detail));
    } finally {
      setPauseLoading(false);
    }
  }, [pauseLoading, pauseNote, previewRow]);

  const pageSubheader = {
    ad: 'Broad campaigns work best at the ad level. Creative evaluation combines Facebook delivery, lead progression, and business-outcome guidance. Clients without backend integrations stop at lead/self-book stages and are excluded from later-stage benchmark math.',
    theme: 'Theme-level creative pattern rollup for broad creative analysis. Use this to spot reusable patterns across the current scope.',
    subtheme: 'Subtheme-level creative pattern rollup for broad creative analysis. Use this to find more specific winners worth briefing into the next round.',
  }[viewMode];

  const backendCapableRows = decisionRows.filter((row) => row.hasBackendIntegration).length;
  const parsedThemeCoverage = decisionRows.length ? safeDiv(decisionRows.filter((row) => row.themeName).length, decisionRows.length) : 0;
  const parsedSubthemeCoverage = decisionRows.length ? safeDiv(decisionRows.filter((row) => row.subthemeName).length, decisionRows.length) : 0;
  const matureCohortCutoffLabel = React.useMemo(() => {
    const rowWithCutoff = filteredRows.find((row) => row.matureEndDate);
    return formatShortDate(rowWithCutoff?.matureEndDate);
  }, [filteredRows]);
  const currentRangeDays = getDateRangeDays(startDate, endDate);
  const recommendedRangeGuidance = React.useMemo(
    () => getRecommendedLookbackGuidance({
      rows: filteredRows,
      recentRows: filteredRecentRows,
      currentRangeDays,
    }),
    [currentRangeDays, filteredRecentRows, filteredRows]
  );
  const showAdDecisionBoard = viewMode === 'ad';
  const showGroupedDecisionBoard = viewMode !== 'ad';

  return (
    <Page title="Creative Performance" fullWidth>
      <Stack spacing={3}>
        {error && <Alert severity="error">{error}</Alert>}

        <Card>
          <CardContent>
            <Stack spacing={2.5}>
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', lg: 'center' }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.05 }}>
                    Creative Performance
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, maxWidth: 980 }}>
                    {pageSubheader}
                  </Typography>
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: 2.5,
                      bgcolor: 'success.main',
                      color: 'common.white',
                      minWidth: 200,
                    }}
                  >
                    <Typography variant="caption" sx={{ opacity: 0.85, display: 'block' }}>
                      Date Range
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      {formatDateToYMD(startDate)} - {formatDateToYMD(endDate)}
                    </Typography>
                  </Box>
                  {viewMode === 'ad' ? (
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1,
                        borderRadius: 2.5,
                        bgcolor: 'info.light',
                        color: 'info.contrastText',
                        minWidth: 240,
                      }}
                    >
                      <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
                        <Tooltip
                          title="Shorter windows are better for high-volume accounts. Longer windows are better when ad-level volume is split across many creatives."
                          arrow
                        >
                          <Box component="span" sx={{ cursor: 'help' }}>
                            Recommended Range
                          </Box>
                        </Tooltip>
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        {recommendedRangeGuidance.label}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', opacity: 0.9, mt: 0.25 }}>
                        {recommendedRangeGuidance.reason}
                      </Typography>
                    </Box>
                  ) : null}
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: 2.5,
                      bgcolor: 'grey.100',
                      color: 'text.primary',
                      minWidth: 160,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Scope
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      {decisionRows.length} {viewMode === 'ad' ? 'rows' : viewMode === 'theme' ? 'themes' : 'subthemes'}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>

              <Stack direction={{ xs: 'column', xl: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', xl: 'center' }}>
                <Box sx={{ minWidth: { xs: '100%', xl: 320 } }}>
                  <DateRangePicker
                    startDate={startDate}
                    endDate={endDate}
                    onChange={handleDateRangeChange}
                    style={{ width: '100%' }}
                  />
                  {viewMode === 'ad' ? (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
                      {recommendedRangeGuidance.detailText}
                    </Typography>
                  ) : null}
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gap: 1.25,
                    flex: 1,
                    gridTemplateColumns: {
                      xs: '1fr',
                      md: 'repeat(2, minmax(220px, 1fr))',
                      xl: 'repeat(4, minmax(180px, 1fr))',
                    },
                  }}
                >
                  <Autocomplete
                    multiple
                    size="small"
                    options={parentCompanyOptions.map((option) => ({
                      label: option.name,
                      value: option.id,
                    }))}
                    value={parentCompanyOptions
                      .filter((option) => selectedParentCompanies.includes(option.id))
                      .map((option) => ({ label: option.name, value: option.id }))}
                    onChange={(_, value) => setSelectedParentCompanies(value.map((option) => option.value))}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    getOptionLabel={(option) => option.label || ''}
                    renderInput={(params) => <TextField {...params} label="Parent Companies" />}
                  />

                  <Autocomplete
                    multiple
                    size="small"
                    options={clientOptions.map((option) => ({
                      label: option.name,
                      value: option.id,
                    }))}
                    value={clientOptions
                      .filter((option) => selectedClients.includes(option.id))
                      .map((option) => ({ label: option.name, value: option.id }))}
                    onChange={(_, value) => setSelectedClients(value.map((option) => option.value))}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    getOptionLabel={(option) => option.label || ''}
                    renderInput={(params) => <TextField {...params} label="Clients" />}
                  />

                </Box>
              </Stack>

              <Box
                sx={{
                  p: 1.25,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.default',
                }}
              >
                <Stack
                  direction={{ xs: 'column', xl: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'stretch', xl: 'center' }}
                  justifyContent="space-between"
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', md: 'center' }} flexWrap="wrap">
                    <ToggleButtonGroup
                      value={viewMode}
                      exclusive
                      size="small"
                      onChange={(_, value) => {
                        if (value) setViewMode(value);
                      }}
                      sx={{
                        '& .MuiToggleButton-root': {
                          border: 0,
                          px: 1.6,
                          py: 0.8,
                          borderRadius: 2,
                          fontWeight: 700,
                        },
                      }}
                    >
                      {VIEW_MODE_OPTIONS.map((option) => (
                        <ToggleButton key={option.key} value={option.key}>
                          {option.label}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>

                    {viewMode === 'ad' ? (
                      <ToggleButtonGroup
                        value={showPausedAds ? 'all' : 'active'}
                        exclusive
                        size="small"
                        onChange={(_, value) => {
                          if (value) setShowPausedAds(value === 'all');
                        }}
                        sx={{
                          '& .MuiToggleButton-root': {
                            border: 0,
                            px: 1.6,
                            py: 0.8,
                            borderRadius: 2,
                            fontWeight: 700,
                          },
                        }}
                      >
                        <ToggleButton value="active">Active Only</ToggleButton>
                        <ToggleButton value="all">Include Paused</ToggleButton>
                      </ToggleButtonGroup>
                    ) : null}
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <ToggleButtonGroup
                      value={backendCoverageFilter}
                      exclusive
                      size="small"
                      onChange={(_, value) => {
                        if (value) setBackendCoverageFilter(value);
                      }}
                      sx={{
                        '& .MuiToggleButton-root': {
                          border: 0,
                          px: 1.6,
                          py: 0.8,
                          borderRadius: 2,
                          fontWeight: 700,
                        },
                      }}
                    >
                      <ToggleButton value="all">All Clients</ToggleButton>
                      <ToggleButton value="backend_only">Backend Only</ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="body2" color="text.secondary">
                      Showing {decisionRows.length} {viewMode === 'ad' ? 'rows' : viewMode === 'theme' ? 'themes' : 'subthemes'}
                    </Typography>
                    {matureCohortCutoffLabel ? (
                      <Typography variant="body2" color="text.secondary">
                        Backend KPIs use mature lead cohorts through {matureCohortCutoffLabel}.
                      </Typography>
                    ) : null}
                  </Stack>
                </Stack>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gap: 1.25,
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, minmax(220px, 1fr))',
                    xl: 'repeat(5, minmax(170px, 1fr))',
                  },
                }}
              >
                <Autocomplete
                  multiple
                  size="small"
                  options={filterOptions.verticals}
                  value={filterOptions.verticals.filter((option) => selectedVerticals.includes(option.value))}
                  onChange={(_, value) => setSelectedVerticals(value.map((option) => option.value))}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  getOptionLabel={(option) => option.label || ''}
                  renderInput={(params) => <TextField {...params} label="Vertical" />}
                />

                <Autocomplete
                  multiple
                  size="small"
                  options={filterOptions.themes}
                  value={filterOptions.themes.filter((option) => selectedThemes.includes(option.value))}
                  onChange={(_, value) => setSelectedThemes(value.map((option) => option.value))}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  getOptionLabel={(option) => option.label || ''}
                  renderInput={(params) => <TextField {...params} label="Theme" />}
                />

                <Autocomplete
                  multiple
                  size="small"
                  options={filterOptions.subthemes}
                  value={filterOptions.subthemes.filter((option) => selectedSubthemes.includes(option.value))}
                  onChange={(_, value) => setSelectedSubthemes(value.map((option) => option.value))}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  getOptionLabel={(option) => option.label || ''}
                  renderInput={(params) => <TextField {...params} label="Subtheme" />}
                />

                <Autocomplete
                  multiple
                  size="small"
                  options={filterOptions.adTypes}
                  value={filterOptions.adTypes.filter((option) => selectedAdTypes.includes(option.value))}
                  onChange={(_, value) => setSelectedAdTypes(value.map((option) => option.value))}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  getOptionLabel={(option) => option.label || ''}
                  renderInput={(params) => <TextField {...params} label="Ad Type" />}
                />

                <Autocomplete
                  multiple
                  size="small"
                  options={filterOptions.notes}
                  value={filterOptions.notes.filter((option) => selectedNotes.includes(option.value))}
                  onChange={(_, value) => setSelectedNotes(value.map((option) => option.value))}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  getOptionLabel={(option) => option.label || ''}
                  renderInput={(params) => <TextField {...params} label="Notes" />}
                />
              </Box>
            </Stack>

          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack
              direction={{ xs: 'column', xl: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', xl: 'center' }}
            >
              <Box sx={{ flex: 1, p: 2, borderRadius: 3, bgcolor: 'grey.100' }}>
                <Typography variant="overline" color="text.secondary">Spend</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  <DollarFormatter value={totals.spend} />
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Across {decisionRows.length} {viewMode === 'ad' ? 'rows' : viewMode}.
                </Typography>
              </Box>

              <Box sx={{ flex: 1, p: 2, borderRadius: 3, bgcolor: 'grey.100' }}>
                <Typography variant="overline" color="text.secondary">Decision KPI</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{DECISION_KPI_OPTION.label}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Auto-switches between predicted and actual CoM based on maturity-adjusted lead cohorts.
                </Typography>
              </Box>

              <Box sx={{ flex: 1, p: 2, borderRadius: 3, bgcolor: 'grey.100' }}>
                <Typography variant="overline" color="text.secondary">Backend Coverage</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{backendCapableRows}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Rows with downstream benchmark eligibility.
                </Typography>
              </Box>

              <Box sx={{ flex: 1, p: 2, borderRadius: 3, bgcolor: 'grey.100' }}>
                <Typography variant="overline" color="text.secondary">Theme Coverage</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>{formatPercentLabel(parsedThemeCoverage)}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Subtheme coverage: {formatPercentLabel(parsedSubthemeCoverage)}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3}>
          {showAdDecisionBoard ? (
            <React.Fragment>
              <Card sx={{ flex: 1 }}>
                <CardHeader title="Top Keep-Running Ads" subheader="Best current ads to leave on based on decision CoM, confidence, and meaningful spend." />
                <CardContent>
                  <Stack spacing={1.5}>
                    {actionQueues.topKeepRunning.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No clear keep-running ads.</Typography>
                    ) : actionQueues.topKeepRunning.map((row) => (
                      <Box key={`keep-running-${row.uuid}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Box>
                          <Typography sx={{ fontWeight: 600 }}>{row.adName}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {row.clientName} • {row.verticalName || 'Unassigned'} • {row.spendShareLabel} share
                          </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 700 }}>{row.overallRecommendation}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
              <Card sx={{ flex: 1 }}>
                <CardHeader title="Top Pause Candidates" subheader="Highest wasted-spend risk based on weak signal, confidence, and spend share." />
                <CardContent>
                  <Stack spacing={1.5}>
                    {actionQueues.topPause.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No clear pause candidates.</Typography>
                    ) : actionQueues.topPause.map((row) => (
                      <Box key={`pause-${row.uuid}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Box>
                          <Typography sx={{ fontWeight: 600 }}>{row.adName}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {row.clientName} • {row.verticalName || 'Unassigned'} • {row.spendShareLabel} share
                          </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 700 }}>{row.pausePriorityLabel}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
              <Card sx={{ flex: 1 }}>
                <CardHeader title="Keep Testing" subheader="Ads with interesting signal but not enough confidence yet to force a decision." />
                <CardContent>
                  <Stack spacing={1.5}>
                    {actionQueues.keepTesting.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No ads currently in test-longer status.</Typography>
                    ) : actionQueues.keepTesting.map((row) => (
                      <Box key={`test-${row.uuid}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Box>
                          <Typography sx={{ fontWeight: 600 }}>{row.adName}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {row.clientName} • {row.verticalName || 'Unassigned'} • {row.confidenceLabel} confidence
                          </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 700 }}>{row.overallRecommendation}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
              <Card sx={{ flex: 1 }}>
                <CardHeader title="Build More Like This" subheader="Creative patterns worth reusing when you brief the next round of ads." />
                <CardContent>
                  <Stack spacing={1.5}>
                    {actionQueues.buildMore.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No strong build-more patterns yet.</Typography>
                    ) : actionQueues.buildMore.map((row) => (
                      <Box key={`build-more-${row.uuid}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Box>
                          <Typography sx={{ fontWeight: 600 }}>{row.adName}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {row.clientName} • {row.themeName || 'No Theme'} • {row.subthemeName || 'No Subtheme'} • {row.facebookFormat || 'Unknown'}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontWeight: 700 }}>{row.buildPriorityLabel}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </React.Fragment>
          ) : null}
        </Stack>

        {showGroupedDecisionBoard ? (
          <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3}>
            <Card sx={{ flex: 1 }}>
              <CardHeader title="Best Grouped Winners" subheader="Top grouped patterns by automatic decision CoM in the active scope." />
              <CardContent>
                <Stack spacing={1.5}>
                  {topPerformers.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No grouped winners yet.</Typography>
                  ) : topPerformers.map((row) => (
                    <Box key={`group-win-${row.uuid}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>{getRowLabel(row, viewMode)}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {row.clientCount ? `${row.clientCount} clients • ` : ''}<DollarFormatter value={row.spend} />
                        </Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 700 }}>
                        {renderMetricValue(
                          row[DECISION_KPI_OPTION.key],
                          METRIC_COLUMNS.find((column) => column.key === DECISION_KPI_OPTION.key)?.type || 'count'
                        )}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1 }}>
              <CardHeader title="Grouped Pause Risks" subheader="Patterns that look weak enough to stop producing or deprioritize." />
              <CardContent>
                <Stack spacing={1.5}>
                  {bottomPerformers.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No grouped pause risks yet.</Typography>
                  ) : bottomPerformers.map((row) => (
                    <Box key={`group-risk-${row.uuid}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 600 }}>{getRowLabel(row, viewMode)}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {row.clientCount ? `${row.clientCount} clients • ` : ''}<DollarFormatter value={row.spend} />
                        </Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 700, color: 'error.main' }}>
                        {renderMetricValue(
                          row[DECISION_KPI_OPTION.key],
                          METRIC_COLUMNS.find((column) => column.key === DECISION_KPI_OPTION.key)?.type || 'count'
                        )}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        ) : null}

        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3}>
          <Card sx={{ flex: 1 }}>
            <CardHeader title="Best Themes" subheader="Highest-confidence theme patterns worth reusing in the current scope." />
            <CardContent>
              <Stack spacing={1.5}>
                {bestThemes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No parsed theme data yet.</Typography>
                ) : bestThemes.map((theme) => (
                  <Box key={`best-theme-${theme.name}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>{theme.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {theme.count} ads • <DollarFormatter value={theme.spend} /> • {theme.confidenceLabel} confidence
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {theme.effectiveCostOfMarketingAvg != null ? (
                          <>Avg Decision CoM <PercentageFormatter value={theme.effectiveCostOfMarketingAvg} /> • </>
                        ) : null}
                        {getPatternRecommendationSummary(theme)}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 700 }}>{theme.rankingScore}</Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ flex: 1 }}>
            <CardHeader title="Formats" subheader="Ranked format performance with confidence and action mix." />
            <CardContent>
              <Stack spacing={1.5}>
                {bestFormats.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No format data yet.</Typography>
                ) : bestFormats.map((format) => (
                  <Box key={`best-format-${format.name}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>{format.name || 'Unknown'}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {format.count} ads • <DollarFormatter value={format.spend} /> • {format.confidenceLabel} confidence
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format.effectiveCostOfMarketingAvg != null ? (
                          <>Avg Decision CoM <PercentageFormatter value={format.effectiveCostOfMarketingAvg} /> • </>
                        ) : null}
                        {getPatternRecommendationSummary(format)}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 700 }}>{format.rankingScore}</Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3}>
          <Card sx={{ flex: 1 }}>
            <CardHeader title="Worst Themes" subheader="Lowest-confidence or weakest theme patterns to avoid or rewrite." />
            <CardContent>
              <Stack spacing={1.5}>
                {worstThemes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No parsed theme data yet.</Typography>
                ) : worstThemes.map((theme) => (
                  <Box key={`worst-theme-${theme.name}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>{theme.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {theme.count} ads • <DollarFormatter value={theme.spend} /> • {theme.confidenceLabel} confidence
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {theme.effectiveCostOfMarketingAvg != null ? (
                          <>Avg Decision CoM <PercentageFormatter value={theme.effectiveCostOfMarketingAvg} /> • </>
                        ) : null}
                        {getPatternRecommendationSummary(theme)}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: 'error.main' }}>{theme.rankingScore}</Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ flex: 1 }}>
            <CardHeader title="Best Subthemes" subheader="Top parsed subthemes worth briefing into the next round of ads." />
            <CardContent>
              <Stack spacing={1.5}>
                {bestSubthemes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No parsed subtheme data yet.</Typography>
                ) : bestSubthemes.map((subtheme) => (
                  <Box key={`best-subtheme-${subtheme.name}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>{subtheme.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {subtheme.count} ads • <DollarFormatter value={subtheme.spend} /> • {subtheme.confidenceLabel} confidence
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {subtheme.effectiveCostOfMarketingAvg != null ? (
                          <>Avg Decision CoM <PercentageFormatter value={subtheme.effectiveCostOfMarketingAvg} /> • </>
                        ) : null}
                        {getPatternRecommendationSummary(subtheme)}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontWeight: 700 }}>{subtheme.rankingScore}</Typography>
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        <Card>
          <CardHeader title="Worst Subthemes" subheader="Subthemes that are repeatedly weak enough to retire, rewrite, or deprioritize." />
          <CardContent>
            <Stack spacing={1.5}>
              {worstSubthemes.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No parsed subtheme data yet.</Typography>
              ) : worstSubthemes.map((subtheme) => (
                <Box key={`worst-subtheme-${subtheme.name}`} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>{subtheme.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {subtheme.count} ads • <DollarFormatter value={subtheme.spend} /> • {subtheme.confidenceLabel} confidence
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {subtheme.effectiveCostOfMarketingAvg != null ? (
                        <>Avg Decision CoM <PercentageFormatter value={subtheme.effectiveCostOfMarketingAvg} /> • </>
                      ) : null}
                      {getPatternRecommendationSummary(subtheme)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 700, color: 'error.main' }}>{subtheme.rankingScore}</Typography>
                </Box>
              ))}
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader
            title={
              viewMode === 'ad'
                ? 'Creative Evaluation Table'
                : viewMode === 'theme'
                  ? 'Theme Evaluation Table'
                  : 'Subtheme Evaluation Table'
            }
            subheader="Decision-first creative evaluation with spend, funnel progression, and business outcome metrics."
          />
          <CardContent sx={{ p: 0 }}>
            {loading || loadingClientMeta ? (
              <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: '74vh' }}>
                <Table stickyHeader size="small" sx={{ minWidth: 2400 }}>
                  <TableHead>
                    <TableRow>
                      {dimensionColumns.map((column, index) => (
                        <TableCell
                          key={column.key}
                          sx={{
                            minWidth: column.minWidth,
                            ...(column.sticky
                              ? {
                                  position: 'sticky',
                                  left: column.stickyLeft || 0,
                                  zIndex: 3,
                                  backgroundColor: 'background.paper',
                                }
                              : {}),
                          }}
                        >
                          {column.helpText ? (
                            <Tooltip title={column.helpText} arrow>
                              <Box component="span" sx={{ cursor: 'help' }}>
                                {column.label}
                              </Box>
                            </Tooltip>
                          ) : (
                            column.label
                          )}
                        </TableCell>
                      ))}
                      {METRIC_COLUMNS.map((column) => (
                        <TableCell key={column.key} sx={{ minWidth: 120 }}>
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {decisionRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={METRIC_COLUMNS.length + dimensionColumns.length} align="center" sx={{ py: 6 }}>
                          No rows match the selected filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {viewMode === 'ad' ? adHierarchy.map((clientGroup) => {
                          const clientExpanded = expandedClients[clientGroup.key] ?? true;
                          return (
                            <React.Fragment key={clientGroup.key}>
                              <TableRow>
                                <TableCell
                                  colSpan={METRIC_COLUMNS.length + dimensionColumns.length}
                                  sx={{ bgcolor: '#f6f8fb', borderBottomColor: 'divider', py: 1.25 }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <IconButton size="small" onClick={() => toggleClientExpanded(clientGroup.key)}>
                                      {clientExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                                    </IconButton>
                                    <Typography sx={{ fontWeight: 800 }}>
                                      {clientGroup.clientName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      {clientGroup.verticalName || 'Unassigned Vertical'} • {clientGroup.rows.length} ads • <DollarFormatter value={clientGroup.spend} />
                                    </Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                              {clientExpanded ? clientGroup.campaigns.map((campaignGroup) => {
                                const campaignExpanded = expandedCampaigns[campaignGroup.key] ?? true;
                                return (
                                  <React.Fragment key={campaignGroup.key}>
                                    <TableRow>
                                      <TableCell
                                        colSpan={METRIC_COLUMNS.length + dimensionColumns.length}
                                        sx={{ bgcolor: '#fbfcfe', borderBottomColor: 'divider', py: 1 }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 3 }}>
                                          <IconButton size="small" onClick={() => toggleCampaignExpanded(campaignGroup.key)}>
                                            {campaignExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                                          </IconButton>
                                          <Typography sx={{ fontWeight: 700 }}>
                                            {campaignGroup.campaign}
                                          </Typography>
                                          <Typography variant="body2" color="text.secondary">
                                            {campaignGroup.rows.length} ads • <DollarFormatter value={campaignGroup.spend} />
                                          </Typography>
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                    {campaignExpanded ? campaignGroup.adSets.map((adSetGroup) => {
                                      const adSetExpanded = expandedAdSets[adSetGroup.key] ?? true;
                                      return (
                                        <React.Fragment key={adSetGroup.key}>
                                          <TableRow>
                                            <TableCell
                                              colSpan={METRIC_COLUMNS.length + dimensionColumns.length}
                                              sx={{ bgcolor: '#ffffff', borderBottomColor: 'divider', py: 0.9 }}
                                            >
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 6 }}>
                                                <IconButton size="small" onClick={() => toggleAdSetExpanded(adSetGroup.key)}>
                                                  {adSetExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                                                </IconButton>
                                                <Typography sx={{ fontWeight: 700 }}>
                                                  {adSetGroup.adSetName}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                  {adSetGroup.rows.length} ads • <DollarFormatter value={adSetGroup.spend} />
                                                </Typography>
                                              </Box>
                                            </TableCell>
                                          </TableRow>
                                          {adSetExpanded ? adSetGroup.rows.map((row) => (
                                            <TableRow key={row.uuid} hover>
                                              {dimensionColumns.map((column) => (
                                                <TableCell
                                                  key={`${row.uuid}-${column.key}`}
                                                  title={column.truncate ? (row[column.key] || '') : undefined}
                                                  sx={
                                                    column.sticky
                                                      ? {
                                                          position: 'sticky',
                                                          left: column.stickyLeft || 0,
                                                          zIndex: 2,
                                                          backgroundColor: 'background.paper',
                                                        }
                                                      : undefined
                                                  }
                                                >
                                                  {renderDimensionValue(column, row, handleOpenPreview)}
                                                </TableCell>
                                              ))}
                                              {METRIC_COLUMNS.map((column) => (
                                                <TableCell key={column.key}>
                                                  {renderMetricValue(row[column.key], column.type)}
                                                </TableCell>
                                              ))}
                                            </TableRow>
                                          )) : null}
                                        </React.Fragment>
                                      );
                                    }) : null}
                                  </React.Fragment>
                                );
                              }) : null}
                            </React.Fragment>
                          );
                        }) : decisionRows.map((row) => (
                          <TableRow key={row.uuid} hover>
                            {dimensionColumns.map((column) => (
                              <TableCell
                                key={`${row.uuid}-${column.key}`}
                                title={column.truncate ? (row[column.key] || '') : undefined}
                                sx={
                                  column.sticky
                                    ? {
                                        position: 'sticky',
                                        left: column.stickyLeft || 0,
                                        zIndex: 2,
                                        backgroundColor: 'background.paper',
                                      }
                                    : undefined
                                }
                              >
                                {renderDimensionValue(column, row, handleOpenPreview)}
                              </TableCell>
                            ))}
                            {METRIC_COLUMNS.map((column) => (
                              <TableCell key={column.key}>
                                {renderMetricValue(row[column.key], column.type)}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                        <TableRow>
                          {dimensionColumns.map((column, index) => (
                            <TableCell
                              key={`total-${column.key}`}
                              sx={{
                                fontWeight: 700,
                                ...(column.sticky
                                  ? {
                                      position: 'sticky',
                                      left: column.stickyLeft || 0,
                                      zIndex: 2,
                                      backgroundColor: 'background.paper',
                                    }
                                  : {}),
                              }}
                            >
                              {index === 0 ? 'Total' : '-'}
                            </TableCell>
                          ))}
                          {METRIC_COLUMNS.map((column) => (
                            <TableCell key={column.key} sx={{ fontWeight: 700 }}>
                              {renderMetricValue(totals[column.key], column.type)}
                            </TableCell>
                          ))}
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        <Dialog open={Boolean(previewRow)} onClose={handleClosePreview} maxWidth="md" fullWidth>
          <DialogTitle>{previewRow?.adName || 'Ad Preview'}</DialogTitle>
          <DialogContent>
            {previewRow ? (
              <Stack spacing={2.5}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">Campaign</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {previewRow.campaign || 'No campaign stored.'}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">Ad Set</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {previewRow.adSetName || 'No ad set stored.'}
                    </Typography>
                  </Box>
                </Stack>
                {previewRow.imageUrl || previewRow.thumbnailUrl ? (
                  <Box
                    component="img"
                    src={previewRow.imageUrl || previewRow.thumbnailUrl}
                    alt={previewRow.adName || 'Ad creative preview'}
                    sx={{
                      width: '100%',
                      maxHeight: 360,
                      objectFit: 'contain',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'grey.100',
                    }}
                  />
                ) : null}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} flexWrap="wrap">
                  {renderStatusPill(previewRow.facebookFormat || 'Unknown', getFormatTone(previewRow.facebookFormat))}
                  {renderStatusPill(previewRow.creativeVerdict || 'Unknown', getVerdictTone(previewRow.creativeVerdict))}
                  {renderStatusPill(previewRow.overallRecommendation || 'Unknown', getRecommendationTone(previewRow.overallRecommendation))}
                  {previewRow.comConfidenceLabel ? renderStatusPill(previewRow.comConfidenceLabel, getComConfidenceTone(previewRow.comConfidenceLabel)) : null}
                  {previewRow.dealValueStability ? renderStatusPill(previewRow.dealValueStability, 'neutral') : null}
                  {previewRow.ctaType ? renderStatusPill(previewRow.ctaType, 'neutral') : null}
                </Stack>
                <Box>
                  <Typography variant="overline" color="text.secondary">Headline</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {previewRow.headline || 'No headline stored.'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="overline" color="text.secondary">Primary Text</Typography>
                  <Typography variant="body2">
                    {previewRow.primaryText || 'No primary text stored.'}
                  </Typography>
                </Box>
                {previewRow.description ? (
                  <Box>
                    <Typography variant="overline" color="text.secondary">Description</Typography>
                    <Typography variant="body2">{previewRow.description}</Typography>
                  </Box>
                ) : null}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">Why</Typography>
                    <Typography variant="body2">{previewRow.whyLabel || '-'}</Typography>
                    {Array.isArray(previewRow.whyDriverChips) && previewRow.whyDriverChips.length ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                        {previewRow.whyDriverChips.map((driver) => (
                          <React.Fragment key={`preview-${driver.key}-${driver.label}`}>
                            {renderStatusPill(formatWhyDriverChip(driver), getWhyDriverTone(driver.directionalDelta))}
                          </React.Fragment>
                        ))}
                      </Box>
                    ) : null}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" color="text.secondary">Destination</Typography>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                      {previewRow.destinationUrl || 'No destination URL stored.'}
                    </Typography>
                  </Box>
                </Stack>
                {previewRow.videoId ? (
                  <Typography variant="body2" color="text.secondary">
                    Stored video id: {previewRow.videoId}
                  </Typography>
                ) : null}
              </Stack>
            ) : null}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <TextField
              label="Final Pause Note"
              value={pauseNote}
              onChange={(event) => setPauseNote(event.target.value)}
              size="small"
              multiline
              minRows={2}
              maxRows={4}
              fullWidth
              disabled={!previewRow?.isActive || pauseLoading}
            />
            <Button onClick={handleClosePreview} disabled={pauseLoading}>
              Close
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handlePauseAd}
              disabled={!previewRow?.isActive || pauseLoading || !previewRow?.adId || !previewRow?.clientId}
            >
              {pauseLoading ? 'Pausing...' : 'Pause Ad'}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Page>
  );
}
